/**
 * IMPORTANTE: jsPDF não suporta emojis Unicode (⏳, ✅, ❌, etc.)
 * Use apenas caracteres ASCII em textos para PDF.
 * Emojis podem ser usados na visualização HTML sem problemas.
 */

import { useState } from "react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from 'jspdf-autotable';
import { useToast } from "@/hooks/use-toast";
import { calculatePlanStatus } from "./use-maintenance-plans";
import { categorizeMaintenanceType, getCategoryInfo, getCategoryLabel, MAINTENANCE_CATEGORIES } from "@/lib/maintenance-categorization";
import type { MaintenanceCategory } from "@/lib/maintenance-categorization";

// Extend jsPDF type to include autoTable
declare module 'jspdf' {
  interface jsPDF {
    autoTable: typeof autoTable;
  }
}

import { formatWorkOrderNumber } from "@/hooks/use-work-orders";
import type { VehicleDetailData } from "./use-vehicle-detail";
import type { TimelineEvent } from "@/components/fleet/reports/VehicleTimeline";
import type { PerformanceMetric, FleetAverages } from "@/components/fleet/reports/VehiclePerformanceComparison";
import type { MaintenancePrediction } from "@/components/fleet/reports/MaintenancePrediction";
import type { EfficiencyData } from "@/components/fleet/reports/FuelEfficiencyAnalysis";
import type { Recommendation } from "@/components/fleet/reports/VehicleRecommendations";

export interface MaintenanceReportAnalytics {
  providerPerformance: Array<{
    providerId: string;
    providerName: string;
    totalOrders: number;
    totalCost: number;
    avgCost: number;
    completionRate: number;
    avgRepairDays: number;
  }>;
  monthlyTrend: Array<{
    month: string;
    totalOrders: number;
    totalCost: number;
    avgCost: number;
  }>;
  costByType: {
    preventive: { count: number; cost: number; percentage: number };
    corrective: { count: number; cost: number; percentage: number };
  };
  criticalVehicles: Array<{
    vehicleId: string;
    vehiclePlate: string;
    maintenanceCount: number;
    totalCost: number;
    lastMaintenance: string;
  }>;
  recommendations: Array<{
    category: 'cost' | 'efficiency' | 'safety' | 'provider';
    priority: 'high' | 'medium' | 'low';
    title: string;
    description: string;
    action: string;
    potentialSavings?: number;
    affectedVehicles?: number;
  }>;
  timelineEvents: Array<{
    date: string;
    type: 'maintenance' | 'alert' | 'inspection';
    title: string;
    vehiclePlate: string;
    provider?: string;
    cost?: number;
    status: string;
    orderNumber?: number;
  }>;
  pendingMaintenance?: {
    plans: Array<{
      vehiclePlate: string;
      planName: string;
      nextExecutionDate: string | null;
      daysUntilMaintenance: number | null;
      kmUntilMaintenance: number | null;
      isOverdue: boolean;
      isCritical: boolean;
    }>;
    totalPending: number;
    overdue: number;
    critical: number;
    estimatedCost: number;
  };
}

export interface ExportConfig {
  data: any[];
  type: 'vehicles' | 'drivers' | 'maintenance' | 'cost-centers' | 'financial' | 'operational' | 'vehicle-detail' | 'documents' | 'tires' | 'fuel' | 'vehicle-maintenance-summary' | 'consolidated' | 'fleet-renewal';
  format: 'excel' | 'csv' | 'pdf';
  filters?: any;
  company?: any;
  filename?: string;
  vehicleDetailData?: VehicleDetailData;
  maintenanceAnalytics?: MaintenanceReportAnalytics;
  fleetReportData?: any; // FleetReportData para relatórios gerenciais
}

export const useReportExport = () => {
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();

  // Helper: Converter HEX para RGB
  const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 37, g: 99, b: 235 }; // Azul padrão
  };

  // Helper: Contar filtros ativos
  const countActiveFilters = (filters?: any): number => {
    if (!filters) return 0;
    let count = 0;
    
    if (filters.dateRange?.from || filters.dateRange?.to) count++;
    if (filters.vehicleIds && filters.vehicleIds.length > 0) count++;
    if (filters.driverIds && filters.driverIds.length > 0) count++;
    if (filters.status && filters.status.length > 0) count++;
    if (filters.maintenanceCategory && filters.maintenanceCategory !== 'all') count++;
    
    return count;
  };

  // Função para adicionar cabeçalho profissional
  const addProfessionalHeader = async (
    doc: jsPDF, 
    config: {
      reportType: string;
      reportTitle: string;
      company: any;
      filters?: any;
      additionalInfo?: string;
    }
  ): Promise<number> => {
    const { reportTitle, company, filters } = config;
    
    let yPos = 10;
    const primaryColor = hexToRgb(company?.branding?.primary_color || '#2563eb');
    
    // ====== FAIXA SUPERIOR SUTIL (30mm de altura) ======
    // Usar uma versão mais clara da cor primária (opacidade visual de 85%)
    const lightColor = {
      r: Math.min(255, primaryColor.r + (255 - primaryColor.r) * 0.85),
      g: Math.min(255, primaryColor.g + (255 - primaryColor.g) * 0.85),
      b: Math.min(255, primaryColor.b + (255 - primaryColor.b) * 0.85)
    };
    
    doc.setFillColor(lightColor.r, lightColor.g, lightColor.b);
    doc.rect(0, 0, 210, 30, 'F'); // Faixa de 30mm (reduzida de 45mm)
    
    // Borda inferior sutil
    doc.setDrawColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.setLineWidth(0.8);
    doc.line(0, 30, 210, 30);
    
    // ====== LOGO DA EMPRESA (menor e mais discreta) ======
    const logoUrl = company?.branding?.logo || company?.logo_url;
    let logoLoaded = false;
    let xStart = 15;
    
    if (logoUrl) {
      try {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = logoUrl;
        
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          setTimeout(reject, 5000);
        });
        
        if (img.complete && img.naturalHeight !== 0) {
          // Logo reduzida: 24x24, centralizada verticalmente
          doc.addImage(img, 'PNG', 15, yPos - 2, 24, 24);
          logoLoaded = true;
          xStart = 42; // Posição após a logo
        }
      } catch (error) {
        console.warn('Nao foi possivel carregar logo:', error);
      }
    }
    
    // ====== NOME DA EMPRESA (mais discreto) ======
    const companyName = company?.name || 'FleetONE';
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16); // Reduzido de 24 para 16
    doc.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b); // Cor da empresa
    doc.text(companyName.toUpperCase(), xStart, yPos + 8);
    
    // ====== CNPJ NO CANTO DIREITO ======
    if (company?.cnpj) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(80, 80, 80);
      const cnpjText = `CNPJ: ${company.cnpj}`;
      const cnpjWidth = doc.getTextWidth(cnpjText);
      doc.text(cnpjText, 195 - cnpjWidth, yPos + 8);
    }
    
    // ====== RAZAO SOCIAL (se disponível) ======
    if (company?.razao_social) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text(company.razao_social, xStart, yPos + 15);
    }
    
    // ====== ENDERECO/CIDADE (se disponível) ======
    const addressParts: string[] = [];
    if (company?.city) addressParts.push(company.city);
    if (company?.state) addressParts.push(company.state);
    
    if (addressParts.length > 0) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(120, 120, 120);
      doc.text(addressParts.join(' - '), xStart, yPos + 20);
    }
    
    // ====== TITULO DO RELATORIO (bem espaçado) ======
    yPos = 40; // Abaixo da faixa (30mm) + margem de 10mm
    
    doc.setFontSize(18); // Tamanho adequado
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.text(reportTitle.toUpperCase(), 15, yPos);
    
    // Linha decorativa abaixo do título
    yPos += 2;
    doc.setDrawColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.setLineWidth(0.5);
    doc.line(15, yPos, 195, yPos);
    
    // ====== METADADOS DO RELATORIO ======
    yPos += 8;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    
    // Data e hora de geração
    const now = new Date();
    const dateStr = now.toLocaleDateString('pt-BR');
    const timeStr = now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    doc.text(`Gerado em: ${dateStr} as ${timeStr}`, 15, yPos);
    
    yPos += 5;
    
    // Período
    if (filters?.dateRange?.from && filters?.dateRange?.to) {
      const startDate = new Date(filters.dateRange.from).toLocaleDateString('pt-BR');
      const endDate = new Date(filters.dateRange.to).toLocaleDateString('pt-BR');
      doc.text(`Periodo: ${startDate} ate ${endDate}`, 15, yPos);
      yPos += 5;
    }
    
    // Tipo de relatório
    if (filters?.maintenanceCategory) {
      const typeMap: Record<string, string> = {
        all: 'Realizadas e Planejadas',
        realized: 'Apenas Realizadas (com OS)',
        planned: 'Apenas Planejadas (sem OS)'
      };
      doc.text(`Tipo: ${typeMap[filters.maintenanceCategory] || filters.maintenanceCategory}`, 15, yPos);
      yPos += 5;
    }
    
    // Filtros ativos
    const activeFiltersCount = countActiveFilters(filters);
    if (activeFiltersCount > 0) {
      doc.text(`Filtros: ${activeFiltersCount} ${activeFiltersCount === 1 ? 'filtro ativo' : 'filtros ativos'}`, 15, yPos);
      yPos += 5;
    }
    
    // Espaço antes do conteúdo
    yPos += 5;
    
    return yPos;
  };

  // Função para adicionar rodapé profissional
  const addProfessionalFooter = (
    doc: jsPDF, 
    pageNum: number, 
    totalPages: number,
    company?: any
  ) => {
    const pageHeight = doc.internal.pageSize.height;
    const primaryColor = hexToRgb(company?.branding?.primary_color || '#2563eb');
    
    // Linha superior do rodapé
    doc.setDrawColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.setLineWidth(0.5);
    doc.line(14, pageHeight - 15, 196, pageHeight - 15);
    
    // Textos do rodapé
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.setFont('helvetica', 'normal');
    
    // Esquerda: Sistema
    doc.text('FleetONE - Sistema de Gestão de Frotas', 14, pageHeight - 10);
    
    // Centro: Empresa
    doc.text(company?.name || '', 105, pageHeight - 10, { align: 'center' });
    
    // Direita: Paginação
    doc.text(`Página ${pageNum} de ${totalPages}`, 196, pageHeight - 10, { align: 'right' });
  };

  // Helper: Traduzir tipos de manutenção para português
  const translateMaintenanceType = (type: string): string => {
    const typeMap: Record<string, string> = {
      'preventive': 'Preventiva',
      'corrective': 'Corretiva',
      'predictive': 'Preditiva',
      'inspection': 'Inspeção',
      'tire': 'Pneus',
      'oil_change': 'Troca de Óleo',
      'brake': 'Freios',
      'suspension': 'Suspensão',
      'electrical': 'Elétrica',
      'engine': 'Motor',
      'transmission': 'Transmissão',
      'body': 'Lataria',
      'other': 'Outros'
    };
    
    return typeMap[type] || type;
  };

  // Helper para estilos de tabela padronizados
  const getTableStyles = (company?: any) => {
    const primaryColor = hexToRgb(company?.branding?.primary_color || '#2563eb');
    
    return {
      theme: 'grid' as const,
      margin: { left: 14, right: 14 },
      tableWidth: 182,
      headStyles: {
        fillColor: [primaryColor.r, primaryColor.g, primaryColor.b] as [number, number, number],
        textColor: [255, 255, 255] as [number, number, number],
        fontStyle: 'bold' as const,
        fontSize: 10,
        halign: 'center' as const,
      },
      styles: {
        fontSize: 9,
        cellPadding: 3,
      },
      alternateRowStyles: {
        fillColor: [245, 247, 250] as [number, number, number],
      },
    };
  };

  // Helper para agrupar planos e OSs por veículo
  interface VehicleMaintenanceGroup {
    vehicle: {
      id: string;
      plate: string;
      make: string;
      model: string;
      cost_center_name?: string;
    };
    plans: Array<{
      id: string;
      name: string;
      type: string;
      status: string;
      calculated_status: string;
      next_execution_date?: string;
      next_execution_km?: number;
      current_odometer?: number;
      periodicity_days?: number;
      periodicity_km?: number;
      work_orders: any[];
    }>;
    totalWorkOrders: number;
    openWorkOrders: number;
  }

  const groupMaintenanceDataByVehicle = (
    plans: any[],
    workOrders: any[]
  ): VehicleMaintenanceGroup[] => {
    const vehicleMap = new Map<string, VehicleMaintenanceGroup>();

    // Primeiro, agrupar planos por veículo
    plans.forEach(plan => {
      const vehicleId = plan.vehicle_id;
      
      if (!vehicleMap.has(vehicleId)) {
        vehicleMap.set(vehicleId, {
          vehicle: {
            id: vehicleId,
            // Aceita ambos formatos: plate (novo) ou vehicle_plate (antigo)
            plate: plan.plate || plan.vehicle_plate || 'Sem Placa',
            make: plan.make || plan.vehicle_model?.split(' ')[0] || '',
            model: plan.model || plan.vehicle_model?.split(' ').slice(1).join(' ') || '',
            cost_center_name: plan.cost_center_name || plan.vehicle_cost_center_name
          },
          plans: [],
          totalWorkOrders: 0,
          openWorkOrders: 0
        });
      }

      const vehicleGroup = vehicleMap.get(vehicleId)!;
      
      // Buscar OSs relacionadas a este plano - aceita ambos formatos de ID
      const planId = plan.plan_id || plan.id;
      const planWorkOrders = workOrders.filter(wo => wo.plan_id === planId);
      const openOrders = planWorkOrders.filter(wo => wo.status === 'open' || wo.status === 'in_progress');

      vehicleGroup.plans.push({
        id: planId,
        // Aceita ambos formatos: name (novo) ou plan_name (antigo)
        name: plan.name || plan.plan_name || 'Sem nome',
        type: plan.type || plan.maintenance_type,
        status: plan.status,
        calculated_status: plan.calculated_status,
        next_execution_date: plan.next_execution_date,
        next_execution_km: plan.next_execution_km,
        current_odometer: plan.current_odometer,
        periodicity_days: plan.periodicity_days,
        periodicity_km: plan.periodicity_km,
        work_orders: planWorkOrders
      });

      vehicleGroup.totalWorkOrders += planWorkOrders.length;
      vehicleGroup.openWorkOrders += openOrders.length;
    });

    // Adicionar OSs sem plano (corretivas avulsas)
    workOrders
      .filter(wo => !wo.plan_id)
      .forEach(wo => {
        const vehicleId = wo.vehicle_id;
        
        if (!vehicleMap.has(vehicleId)) {
          vehicleMap.set(vehicleId, {
            vehicle: {
              id: vehicleId,
              // Aceita ambos formatos: plate (novo) ou vehicle_plate (antigo)
              plate: wo.plate || wo.vehicle_plate || 'Sem Placa',
              make: wo.make || wo.vehicle_model?.split(' ')[0] || '',
              model: wo.model || wo.vehicle_model?.split(' ').slice(1).join(' ') || '',
              cost_center_name: wo.cost_center_name || wo.vehicle_cost_center_name
            },
            plans: [],
            totalWorkOrders: 0,
            openWorkOrders: 0
          });
        }

        const vehicleGroup = vehicleMap.get(vehicleId)!;
        
        // Criar plano virtual para OSs sem plano
        const existingNoPlan = vehicleGroup.plans.find(p => p.id === 'no-plan');
        if (!existingNoPlan) {
          vehicleGroup.plans.push({
            id: 'no-plan',
            name: 'Manutenções sem Plano',
            type: 'corrective',
            status: 'active',
            calculated_status: 'active',
            work_orders: []
          });
        }
        
        const noPlanGroup = vehicleGroup.plans.find(p => p.id === 'no-plan')!;
        noPlanGroup.work_orders.push(wo);
        
        vehicleGroup.totalWorkOrders++;
        if (wo.status === 'open' || wo.status === 'in_progress') {
          vehicleGroup.openWorkOrders++;
        }
      });

    // Ordenar planos por status (em andamento primeiro, depois ativos, depois outros)
    Array.from(vehicleMap.values()).forEach(group => {
      group.plans.sort((a, b) => {
        const statusOrder: Record<string, number> = { in_progress: 1, active: 2, inactive: 3, completed: 4 };
        return (statusOrder[a.calculated_status] || 5) - (statusOrder[b.calculated_status] || 5);
      });
    });

    return Array.from(vehicleMap.values()).sort((a, b) => 
      a.vehicle.plate.localeCompare(b.vehicle.plate)
    );
  };

  const generatePdfBlob = async (config: ExportConfig): Promise<Blob> => {
    setIsExporting(true);

    try {
      const doc = new jsPDF();
      
      // Chamar função de geração baseada no tipo
      switch (config.type) {
        case 'vehicles':
          await generateDetailedVehiclesPDF(doc, config);
          break;
        case 'drivers':
          await generateDriversPDF(doc, config);
          break;
        case 'maintenance':
          await generateMaintenanceUnifiedPDF(doc, config);
          break;
        case 'cost-centers':
          await generateCostCentersPDF(doc, config);
          break;
        case 'financial':
          await generateFinancialPDF(doc, config);
          break;
        case 'operational':
          await generateOperationalPDF(doc, config);
          break;
        case 'documents':
          await generateDocumentsPDF(doc, config);
          break;
        case 'tires':
          await generateTiresPDF(doc, config);
          break;
        case 'fuel':
          await generateFuelPDF(doc, config);
          break;
        case 'vehicle-maintenance-summary':
          await generateVehicleMaintenanceSummaryPDF(doc, config);
          break;
        case 'consolidated':
          await generateConsolidatedPDF(doc, config);
          break;
        case 'fleet-renewal':
          await generateFleetRenewalPDF(doc, config);
          break;
        default:
          throw new Error(`Tipo de relatório não suportado: ${config.type}`);
      }

      // Adicionar rodapé profissional em todas as páginas
      const pageCount = (doc as any).internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        addProfessionalFooter(doc, i, pageCount, config.company);
      }

      // Retornar como Blob (NÃO salvar)
      const blob = doc.output('blob');
      return blob;

    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      throw error;
    } finally {
      setIsExporting(false);
    }
  };

  const generateDetailedVehiclesPDF = async (doc: jsPDF, config: ExportConfig) => {
    const { data, filters, company } = config;
    const primaryColor = hexToRgb(company?.branding?.primary_color || '#2563eb');
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    // Importar helpers do arquivo auxiliar
    const checkPageBreakLocal = (currentY: number, requiredSpace: number = 40): number => {
      if (currentY > pageHeight - requiredSpace - 20) {
        doc.addPage();
        return 25;
      }
      return currentY;
    };

    const addSectionHeaderLocal = (title: string, y: number): number => {
      doc.setFillColor(primaryColor.r, primaryColor.g, primaryColor.b);
      doc.rect(14, y - 5, 4, 8, 'F');
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b);
      doc.text(title, 20, y);
      doc.setDrawColor(primaryColor.r, primaryColor.g, primaryColor.b);
      doc.setLineWidth(0.5);
      doc.line(20, y + 3, 196, y + 3);
      return y + 10;
    };

    // ====== PÁGINA 1: CAPA EXECUTIVA ======
    let yPos = 50;
    
    // Logo
    const logoUrl = company?.branding?.logo || company?.logo_url;
    if (logoUrl) {
      try {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = logoUrl;
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          setTimeout(reject, 3000);
        });
        if (img.complete && img.naturalHeight !== 0) {
          doc.addImage(img, 'PNG', (pageWidth - 50) / 2, yPos, 50, 25);
          yPos += 35;
        }
      } catch (e) {}
    }

    doc.setFontSize(28);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.text('RELATORIO DETALHADO', pageWidth / 2, yPos + 20, { align: 'center' });
    doc.text('DE VEICULOS', pageWidth / 2, yPos + 35, { align: 'center' });

    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text('Informacoes Cadastrais e Operacionais', pageWidth / 2, yPos + 50, { align: 'center' });

    yPos += 65;
    doc.setDrawColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.setLineWidth(1);
    doc.line(40, yPos, pageWidth - 40, yPos);

    yPos += 20;
    doc.setFontSize(12);
    doc.setTextColor(60, 60, 60);
    doc.text(`Empresa: ${company?.name || 'N/A'}`, pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;
    doc.text(`Total de Veiculos: ${data.length}`, pageWidth / 2, yPos, { align: 'center' });

    const now = new Date();
    yPos += 10;
    doc.text(`Gerado em: ${now.toLocaleDateString('pt-BR')} as ${now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`, pageWidth / 2, yPos, { align: 'center' });

    yPos = pageHeight - 50;
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    doc.text('Relatorio gerado automaticamente pelo FleetONE.', pageWidth / 2, yPos, { align: 'center' });

    // ====== PÁGINA 2: RESUMO EXECUTIVO ======
    doc.addPage();
    yPos = 25;

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.text('RESUMO EXECUTIVO', 14, yPos);
    doc.setDrawColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.setLineWidth(0.5);
    doc.line(14, yPos + 3, 100, yPos + 3);
    yPos += 20;

    // Calcular KPIs
    const totalVehicles = data.length;
    const activeVehicles = data.filter(v => v.status === 'active').length;
    const inMaintenance = data.filter(v => v.status === 'maintenance').length;
    const totalFuelCost = data.reduce((sum, v) => sum + (v.fuel_logs?.reduce((s: number, l: any) => s + ((l.liters || 0) * (l.price_unit || 0)), 0) || 0), 0);
    const totalMaintenanceCost = data.reduce((sum, v) => sum + (v.work_orders?.reduce((s: number, wo: any) => s + (wo.total_cost || 0), 0) || 0), 0);
    const totalKm = data.reduce((sum, v) => sum + (v.odometer || 0), 0);

    // KPI Cards
    const kpiCards = [
      { label: 'Total Veiculos', value: String(totalVehicles), color: [59, 130, 246] as [number, number, number] },
      { label: 'Ativos', value: String(activeVehicles), color: [16, 185, 129] as [number, number, number] },
      { label: 'Em Manutencao', value: String(inMaintenance), color: [245, 158, 11] as [number, number, number] },
      { label: 'Km Total', value: `${totalKm.toLocaleString('pt-BR')} km`, color: [139, 92, 246] as [number, number, number] },
      { label: 'Custo Combustivel', value: formatCurrency(totalFuelCost), color: [16, 185, 129] as [number, number, number] },
      { label: 'Custo Manutencao', value: formatCurrency(totalMaintenanceCost), color: [220, 38, 38] as [number, number, number] }
    ];

    const cardWidth = 58;
    const cardHeight = 35;
    const cardSpacing = 5;
    const startX = 14;

    kpiCards.forEach((card, idx) => {
      const col = idx % 3;
      const row = Math.floor(idx / 3);
      const x = startX + col * (cardWidth + cardSpacing);
      const y = yPos + row * (cardHeight + cardSpacing);

      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.3);
      doc.rect(x, y, cardWidth, cardHeight, 'FD');

      doc.setFillColor(card.color[0], card.color[1], card.color[2]);
      doc.rect(x, y, cardWidth, 4, 'F');

      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text(card.label, x + cardWidth / 2, y + 12, { align: 'center' });

      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(40, 40, 40);
      doc.text(card.value, x + cardWidth / 2, y + 25, { align: 'center' });
    });

    yPos += 2 * (cardHeight + cardSpacing) + 15;

    // ====== PÁGINA 3: INSIGHTS ======
    doc.addPage();
    yPos = 25;

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.text('PRINCIPAIS INSIGHTS', 14, yPos);
    doc.setDrawColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.setLineWidth(0.5);
    doc.line(14, yPos + 3, 110, yPos + 3);
    yPos += 20;

    // Gerar insights
    const insights: string[] = [];
    insights.push(`Total de ${data.length} veiculo(s) analisado(s).`);
    if (activeVehicles > 0) insights.push(`${activeVehicles} veiculo(s) ativo(s) na frota.`);
    if (inMaintenance > 0) insights.push(`${inMaintenance} veiculo(s) em manutencao requerem atencao.`);
    if (totalFuelCost > 0) insights.push(`Custo total de combustivel: ${formatCurrency(totalFuelCost)}.`);
    if (totalMaintenanceCost > 0) insights.push(`Custo total de manutencao: ${formatCurrency(totalMaintenanceCost)}.`);

    const withKm = data.filter(v => v.odometer > 0);
    if (withKm.length > 0) {
      const mostKm = withKm.sort((a, b) => b.odometer - a.odometer)[0];
      insights.push(`Veiculo com maior odometro: ${mostKm.plate} - ${formatDistance(mostKm.odometer)}.`);
    }

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(40, 40, 40);

    insights.forEach(insight => {
      doc.setFillColor(primaryColor.r, primaryColor.g, primaryColor.b);
      doc.circle(18, yPos - 1.5, 2, 'F');
      const lines = doc.splitTextToSize(insight, 170);
      doc.text(lines, 24, yPos);
      yPos += lines.length * 6 + 4;
    });

    // ====== PÁGINAS 4+: DETALHES POR VEÍCULO ======
    for (let i = 0; i < data.length; i++) {
      const vehicle = data[i];
      doc.addPage();
      yPos = 20;

      // Cabeçalho do veículo
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b);
      doc.text(`${vehicle.make || ''} ${vehicle.model || ''} - ${vehicle.plate || ''}`, 14, yPos);
      yPos += 12;

      // Informações cadastrais
      yPos = addSectionHeaderLocal('INFORMACOES CADASTRAIS', yPos);

      autoTable(doc, {
        body: [
          ['Placa', vehicle.plate || 'N/A', 'Chassi', vehicle.chassis || 'N/A'],
          ['Fabricante', vehicle.make || 'N/A', 'Modelo', vehicle.model || 'N/A'],
          ['Ano/Modelo', vehicle.year?.toString() || 'N/A', 'Cor', vehicle.color || 'N/A'],
          ['Renavam', vehicle.renavam || 'N/A', 'Categoria', translateCategory(vehicle.category)],
          ['Odometro Atual', formatDistance(vehicle.odometer || 0), 'Status', translateStatus(vehicle.status)],
          ['Data Aquisicao', vehicle.acquisition_date ? formatDate(vehicle.acquisition_date) : 'N/A', 'Valor Aquisicao', vehicle.acquisition_value ? formatCurrency(vehicle.acquisition_value) : 'N/A']
        ],
        startY: yPos,
        theme: 'grid',
        margin: { left: 14, right: 14 },
        tableWidth: 182,
        styles: { fontSize: 9, cellPadding: 4 },
        columnStyles: {
          0: { cellWidth: 45, fontStyle: 'bold', fillColor: [240, 240, 240] as [number, number, number] },
          1: { cellWidth: 46 },
          2: { cellWidth: 45, fontStyle: 'bold', fillColor: [240, 240, 240] as [number, number, number] },
          3: { cellWidth: 46 }
        }
      });

      yPos = (doc as any).lastAutoTable.finalY + 15;

      // Abastecimentos
      if (vehicle.fuel_logs && vehicle.fuel_logs.length > 0) {
        yPos = checkPageBreakLocal(yPos, 60);
        yPos = addSectionHeaderLocal('ABASTECIMENTOS', yPos);

        const totalLiters = vehicle.fuel_logs.reduce((sum: number, log: any) => sum + (log.liters || 0), 0);
        const totalCost = vehicle.fuel_logs.reduce((sum: number, log: any) => sum + ((log.liters || 0) * (log.price_unit || 0)), 0);

        // Mini KPIs
        const fuelKpis = [
          { label: 'Total', value: `${vehicle.fuel_logs.length}`, color: [37, 99, 235] },
          { label: 'Litros', value: `${totalLiters.toFixed(0)}L`, color: [16, 185, 129] },
          { label: 'Custo', value: formatCurrency(totalCost), color: [245, 158, 11] }
        ];

        let kpiX = 14;
        fuelKpis.forEach(kpi => {
          doc.setFillColor(kpi.color[0], kpi.color[1], kpi.color[2]);
          doc.roundedRect(kpiX, yPos, 56, 12, 2, 2, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          doc.text(kpi.label, kpiX + 28, yPos + 4, { align: 'center' });
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.text(kpi.value, kpiX + 28, yPos + 9, { align: 'center' });
          kpiX += 60;
        });

        yPos += 18;

        autoTable(doc, {
          head: [['Data', 'Posto', 'Litros', 'R$/L', 'Total', 'KM']],
          body: vehicle.fuel_logs.slice(0, 10).map((log: any) => [
            formatDate(log.occurred_at),
            log.fleet_providers?.name || 'N/A',
            `${(log.liters || 0).toFixed(2)}L`,
            formatCurrency(log.price_unit || 0),
            formatCurrency((log.liters || 0) * (log.price_unit || 0)),
            formatDistance(log.odometer || 0)
          ]),
          startY: yPos,
          ...getTableStyles(company),
          columnStyles: {
            0: { cellWidth: 30 },
            1: { cellWidth: 40 },
            2: { cellWidth: 25, halign: 'right' },
            3: { cellWidth: 25, halign: 'right' },
            4: { cellWidth: 32, halign: 'right' },
            5: { cellWidth: 30, halign: 'right' }
          }
        });

        yPos = (doc as any).lastAutoTable.finalY + 12;
      }

      // Planos de manutenção
      if (vehicle.maintenance_plans && vehicle.maintenance_plans.length > 0) {
        yPos = checkPageBreakLocal(yPos, 60);
        yPos = addSectionHeaderLocal('PLANOS DE MANUTENCAO', yPos);

        autoTable(doc, {
          head: [['Plano', 'Tipo', 'Prox. Data', 'Prox. KM', 'Periodicidade']],
          body: vehicle.maintenance_plans.map((plan: any) => [
            plan.name || '-',
            translateMaintenanceType(plan.type),
            plan.next_execution_date ? formatDate(plan.next_execution_date) : '-',
            plan.next_execution_km ? formatDistance(plan.next_execution_km) : '-',
            `${plan.periodicity_days || '-'} dias / ${plan.periodicity_km || '-'} km`
          ]),
          startY: yPos,
          ...getTableStyles(company),
          columnStyles: {
            0: { cellWidth: 35 },
            1: { cellWidth: 30 },
            2: { cellWidth: 28 },
            3: { cellWidth: 28 },
            4: { cellWidth: 61 }
          }
        });

        yPos = (doc as any).lastAutoTable.finalY + 12;
      }

      // Alertas ativos
      if (vehicle.alerts && vehicle.alerts.length > 0) {
        yPos = checkPageBreakLocal(yPos, 60);

        doc.setFillColor(220, 53, 69);
        doc.rect(14, yPos - 5, 4, 8, 'F');
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(220, 53, 69);
        doc.text('ALERTAS ATIVOS', 20, yPos);
        doc.setDrawColor(220, 53, 69);
        doc.setLineWidth(0.5);
        doc.line(20, yPos + 3, 196, yPos + 3);
        yPos += 10;

        autoTable(doc, {
          head: [['Tipo', 'Prioridade', 'Descricao', 'Data']],
          body: vehicle.alerts.map((alert: any) => [
            alert.alert_type || '-',
            alert.priority || '-',
            alert.description || '-',
            formatDate(alert.triggered_at)
          ]),
          startY: yPos,
          theme: 'grid',
          margin: { left: 14, right: 14 },
          tableWidth: 182,
          headStyles: {
            fillColor: [220, 53, 69] as [number, number, number],
            textColor: [255, 255, 255] as [number, number, number],
            fontStyle: 'bold' as const,
            fontSize: 9
          },
          styles: { fontSize: 8, cellPadding: 2.5 },
          columnStyles: {
            0: { cellWidth: 35 },
            1: { cellWidth: 25, halign: 'center' },
            2: { cellWidth: 82 },
            3: { cellWidth: 40 }
          }
        });

        yPos = (doc as any).lastAutoTable.finalY + 12;
      }
    }

    // ====== ÚLTIMA PÁGINA: CONCLUSÃO ======
    doc.addPage();
    yPos = 25;

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.text('CONCLUSAO EXECUTIVA', 14, yPos);
    doc.setDrawColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.setLineWidth(0.5);
    doc.line(14, yPos + 3, 110, yPos + 3);
    yPos += 15;

    const inactive = data.filter(v => v.status === 'inactive').length;
    const summary = `A frota conta com ${data.length} veiculo(s) cadastrado(s), sendo ${activeVehicles} ativo(s), ${inMaintenance} em manutencao e ${inactive} inativo(s).`;

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(40, 40, 40);
    const summaryLines = doc.splitTextToSize(summary, 175);
    doc.text(summaryLines, 14, yPos);
    yPos += summaryLines.length * 6 + 10;

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.text('Recomendacoes:', 14, yPos);
    yPos += 10;

    const recommendations: string[] = [];
    if (inMaintenance > 0) recommendations.push('Priorizar conclusao das manutencoes em andamento.');
    if (inactive > 0) recommendations.push('Avaliar veiculos inativos para reativacao ou descarte.');
    recommendations.push('Manter atualizacao regular do odometro de todos os veiculos.');
    recommendations.push('Revisar planos de manutencao preventiva periodicamente.');

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(40, 40, 40);

    recommendations.forEach((rec, idx) => {
      doc.setFillColor(primaryColor.r, primaryColor.g, primaryColor.b);
      doc.circle(18, yPos - 1.5, 3, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8);
      doc.text(String(idx + 1), 18, yPos, { align: 'center' });

      doc.setTextColor(40, 40, 40);
      doc.setFontSize(10);
      const lines = doc.splitTextToSize(rec, 168);
      doc.text(lines, 26, yPos);
      yPos += lines.length * 5 + 4;
    });
  };

  const generateDriversPDF = async (doc: jsPDF, config: ExportConfig) => {
    const { data, filters, company } = config;

    const yPos = await addProfessionalHeader(doc, {
      reportType: 'drivers',
      reportTitle: 'Relatório de Motoristas',
      company,
      filters
    });
    
    const tableData = data.map(d => [
      d.name || 'N/A',
      d.cnh || 'N/A',
      d.phone || 'N/A',
      translateStatus(d.status)
    ]);

    autoTable(doc, {
      head: [['Nome', 'CNH', 'Telefone', 'Status']],
      body: tableData,
      startY: yPos,
      ...getTableStyles(company),
    });
  };

  const generateMaintenanceUnifiedPDF = async (doc: jsPDF, config: ExportConfig) => {
    const { data, filters, company } = config;
    
    let yPos = await addProfessionalHeader(doc, {
      reportType: 'maintenance',
      reportTitle: 'Relatório Completo de Manutenção',
      company,
      filters
    });

    const maintenanceCategory = filters?.maintenanceCategory || 'all';
    
    // Separar dados - suporta tanto formato objeto { plans, workOrders } quanto array legado
    let realizedMaintenance: any[] = [];
    let plannedMaintenance: any[] = [];

    if (Array.isArray(data)) {
      // Formato legado: array único com propriedade type
      realizedMaintenance = data.filter((m: any) => m.type === 'realized') || [];
      plannedMaintenance = data.filter((m: any) => m.type === 'planned') || [];
    } else if (data && typeof data === 'object') {
      // Novo formato: { plans, workOrders }
      const objData = data as { plans?: any[]; workOrders?: any[] };
      plannedMaintenance = (objData.plans || []).map((p: any) => ({ ...p, type: 'planned' }));
      realizedMaintenance = (objData.workOrders || []).map((wo: any) => ({ ...wo, type: 'realized' }));
    }
    
    // Calcular status dinâmico para os planos baseado em suas OSs
    const plannedWithCalculatedStatus = plannedMaintenance.map(plan => {
      const planWorkOrders = realizedMaintenance.filter(wo => wo.plan_id === plan.plan_id);
      const hasInProgress = planWorkOrders.some(wo => wo.status === 'in_progress');
      
      return {
        ...plan,
        calculated_status: hasInProgress ? 'in_progress' : plan.status
      };
    });
    
    // Agrupar por veículo
    const groupedData = groupMaintenanceDataByVehicle(plannedWithCalculatedStatus, realizedMaintenance);

    if (groupedData.length === 0) {
      doc.setFontSize(12);
      doc.setTextColor(100);
      doc.text('Nenhuma manutenção encontrada para os filtros selecionados.', 14, yPos);
      return;
    }

    // KPIs Gerais
    const totalVehicles = groupedData.length;
    const totalPlans = groupedData.reduce((sum, g) => sum + g.plans.length, 0);
    const totalWorkOrders = groupedData.reduce((sum, g) => sum + g.totalWorkOrders, 0);
    const totalOpenOrders = groupedData.reduce((sum, g) => sum + g.openWorkOrders, 0);
    const totalCost = realizedMaintenance.reduce((sum: number, m: any) => sum + (m.total_cost || 0), 0);

    // Resumo Executivo em caixa destacada
    doc.setDrawColor(79, 70, 229); // Roxo
    doc.setLineWidth(0.5);
    doc.rect(14, yPos - 2, 182, 20, 'S'); // Borda

    doc.setFillColor(249, 250, 251); // Cinza muito claro
    doc.rect(14, yPos - 2, 182, 8, 'F'); // Header do resumo

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(79, 70, 229);
    doc.text('RESUMO EXECUTIVO', 16, yPos + 3);

    yPos += 10;

    // KPIs em grid
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60);

    const kpiItems = [
      { label: 'Veiculos:', value: totalVehicles },
      { label: 'Planos:', value: totalPlans },
      { label: 'OSs Total:', value: totalWorkOrders },
      { label: 'OSs Abertas:', value: totalOpenOrders },
      { label: 'Custo Total:', value: formatCurrency(totalCost) }
    ];

    let xPos = 16;
    kpiItems.forEach((kpi, idx) => {
      doc.setFont('helvetica', 'bold');
      doc.text(kpi.label, xPos, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(String(kpi.value), xPos + doc.getTextWidth(kpi.label) + 2, yPos);
      xPos += 36;
      
      // Separador vertical (exceto no último)
      if (idx < kpiItems.length - 1) {
        doc.setDrawColor(200);
        doc.line(xPos - 2, yPos - 3, xPos - 2, yPos + 2);
      }
    });

    yPos += 10;

    // Renderizar cada veículo
    for (const vehicleGroup of groupedData) {
      // Verificar espaço na página
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }

      // Header do Veículo com borda dupla
      doc.setLineWidth(1);
      doc.setDrawColor(0, 102, 204); // Azul escuro
      doc.rect(14, yPos - 6, 182, 14, 'S'); // Borda externa

      doc.setFillColor(240, 248, 255); // Azul muito claro
      doc.rect(14, yPos - 6, 182, 14, 'F'); // Fundo

      // Barra lateral de destaque
      doc.setFillColor(0, 102, 204);
      doc.rect(14, yPos - 6, 3, 14, 'F'); // Barra azul à esquerda

      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 102, 204);
      doc.text('VEICULO:', 20, yPos);

      doc.setTextColor(0);
      doc.text(vehicleGroup.vehicle.plate || 'Sem Placa', 40, yPos);

      const vehicleMake = vehicleGroup.vehicle.make || '';
      const vehicleModel = vehicleGroup.vehicle.model || '';
      if (vehicleMake || vehicleModel) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(80);
        doc.text(`${vehicleMake} ${vehicleModel}`.trim() || '-', 70, yPos);
      }

      // Centro de Custo
      if (vehicleGroup.vehicle.cost_center_name) {
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(90);
        doc.text(`Centro de Custo: ${vehicleGroup.vehicle.cost_center_name || 'N/A'}`, 70, yPos + 4);
      }

      // Badges à direita
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(100);
      doc.text(`Planos: ${vehicleGroup.plans.length}`, 140, yPos - 1);
      doc.text(`OSs: ${vehicleGroup.totalWorkOrders}`, 165, yPos - 1);

      if (vehicleGroup.openWorkOrders > 0) {
        doc.setTextColor(220, 53, 69); // Vermelho
        doc.text(`Abertas: ${vehicleGroup.openWorkOrders}`, 152, yPos + 4);
      }

      yPos += 12;

      // Renderizar cada plano do veículo
      for (const plan of vehicleGroup.plans) {
        // Verificar espaço (aumentar necessidade devido ao layout maior)
        if (yPos > 235) {
          doc.addPage();
          yPos = 20;
        }

        // Calcular se há alerta de vencimento para ajustar altura do card
        let hasExpirationAlert = false;
        
        // Verificar DATA (30 dias)
        if (plan.next_execution_date) {
          const nextDate = new Date(plan.next_execution_date);
          const today = new Date();
          const diffTime = nextDate.getTime() - today.getTime();
          const daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          if (daysRemaining >= 0 && daysRemaining <= 30) {
            hasExpirationAlert = true;
          }
        }
        
        // Verificar KM (1000 km)
        if (plan.next_execution_km !== null && plan.next_execution_km !== undefined) {
          const currentOdometer = plan.current_odometer || 0;
          const kmRemaining = plan.next_execution_km - currentOdometer;
          if (kmRemaining >= 0 && kmRemaining <= 1000) {
            hasExpirationAlert = true;
          }
        }

        // Altura dinâmica do card
        const cardHeight = hasExpirationAlert ? 23 : 18;

        // Caixa do Plano com altura dinâmica
        doc.setLineWidth(0.3);
        doc.setDrawColor(180, 180, 180);
        doc.rect(20, yPos - 4, 172, cardHeight, 'S');

        doc.setFillColor(252, 252, 252); // Cinza clarinho
        doc.rect(20, yPos - 4, 172, 6, 'F'); // Header do plano

        // Bullet point (círculo preenchido)
        doc.setFillColor(100, 100, 100);
        doc.circle(24, yPos - 0.5, 1, 'F');

        // LINHA 1: Nome do Plano
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0);
        doc.text(plan.name || 'Plano sem nome', 28, yPos);

        yPos += 6;

        // LINHA 2: Tipo + Periodicidade (esquerda) e Status (direita)
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(80);

        // Tipo
        const typeText = `Tipo: ${translateMaintenanceType(plan.type) || 'N/A'}`;
        doc.text(typeText, 26, yPos);

        // Periodicidade (ao lado do tipo)
        let periodicityText = 'Periodicidade: ';
        const periodicityParts: string[] = [];

        if (plan.periodicity_days) {
          periodicityParts.push(`${plan.periodicity_days} dias`);
        }
        if (plan.periodicity_km) {
          periodicityParts.push(`${formatDistance(plan.periodicity_km)}`);
        }

        if (periodicityParts.length > 0) {
          periodicityText += periodicityParts.join(' / ');
        } else {
          periodicityText += 'Nao definida';
        }

        const typeWidth = doc.getTextWidth(typeText);
        doc.text(periodicityText, 26 + typeWidth + 5, yPos);

        // Status (alinhado à direita com badge colorido)
        const statusText = plan.calculated_status === 'in_progress' ? 'EM ANDAMENTO' :
                          plan.calculated_status === 'inactive' ? 'INATIVO' :
                          plan.calculated_status === 'completed' ? 'CONCLUIDO' : 'ATIVO';

        const statusColors: Record<string, { bg: [number, number, number], text: [number, number, number] }> = {
          in_progress: { bg: [76, 175, 80], text: [255, 255, 255] }, // Verde
          active: { bg: [33, 150, 243], text: [255, 255, 255] }, // Azul
          inactive: { bg: [158, 158, 158], text: [255, 255, 255] }, // Cinza
          completed: { bg: [156, 39, 176], text: [255, 255, 255] } // Roxo
        };

        const statusColor = statusColors[plan.calculated_status] || statusColors.active;
        const statusWidth = doc.getTextWidth(statusText) + 4;
        const statusX = 192 - statusWidth - 6; // Alinhado à direita com margem

        // Label "Status:" antes do badge
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(80);
        const statusLabelWidth = doc.getTextWidth('Status: ');
        doc.text('Status:', statusX - statusLabelWidth - 2, yPos);

        // Badge do status
        doc.setFillColor(...statusColor.bg);
        doc.roundedRect(statusX, yPos - 3, statusWidth, 4, 1, 1, 'F');
        doc.setTextColor(...statusColor.text);
        doc.setFont('helvetica', 'bold');
        doc.text(statusText, statusX + 2, yPos);

        yPos += 5;

        // LINHA 3: Informações de Execução com ALERTA DE VENCIMENTO
        const executionInfo: string[] = [];

        // Detectar se está próximo ao vencimento
        let isNearingExpiration = false;
        let expirationMessage = '';

        // Verificar por DATA (30 dias)
        if (plan.next_execution_date) {
          const nextDate = new Date(plan.next_execution_date);
          const today = new Date();
          const diffTime = nextDate.getTime() - today.getTime();
          const daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          
          if (daysRemaining >= 0 && daysRemaining <= 30) {
            isNearingExpiration = true;
            expirationMessage = daysRemaining === 0 
              ? 'VENCE HOJE!' 
              : daysRemaining === 1 
                ? 'Vence amanha' 
                : `Vence em ${daysRemaining} dias`;
          }
          
          executionInfo.push(`Prox. Execucao: ${formatDate(plan.next_execution_date)}`);
        }

        // Verificar por KM (1000 km)
        if (plan.next_execution_km !== null && plan.next_execution_km !== undefined) {
          const currentOdometer = plan.current_odometer || 0;
          const kmRemaining = plan.next_execution_km - currentOdometer;
          
          if (kmRemaining >= 0 && kmRemaining <= 1000) {
            isNearingExpiration = true;
            expirationMessage = kmRemaining < 100 
              ? `Faltam ${Math.round(kmRemaining)} km` 
              : `Faltam ${formatDistance(kmRemaining)}`;
          }
          
          executionInfo.push(`Prox. KM: ${formatDistance(plan.next_execution_km)}`);
        }

        if (plan.current_odometer) {
          executionInfo.push(`KM Atual: ${formatDistance(plan.current_odometer)}`);
        }

        // RENDERIZAR ALERTA SE NECESSÁRIO
        if (isNearingExpiration) {
          // Badge "VENCIMENTO PRÓXIMO" centralizado no card
          const badgeWidth = 40;
          const badgeX = 20 + (172 - badgeWidth) / 2; // Centralizado
          
          doc.setFillColor(220, 53, 69); // Vermelho
          doc.roundedRect(badgeX, yPos - 3, badgeWidth, 4, 0.5, 0.5, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(7);
          doc.text('VENCIMENTO PROXIMO', badgeX + badgeWidth / 2, yPos, { align: 'center' });
          
          yPos += 5;
        }

        // Informações de execução (VERMELHO E NEGRITO se próximo ao vencimento)
        if (executionInfo.length > 0) {
          if (isNearingExpiration) {
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(220, 53, 69); // Vermelho
            doc.setFontSize(7.5);
            
            // Adicionar mensagem de urgência junto com as outras informações
            if (expirationMessage) {
              executionInfo.push(expirationMessage);
            }
          } else {
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(100);
            doc.setFontSize(7);
          }
          
          // Todas as informações em uma linha compacta, dentro do card
          const fullText = executionInfo.length > 0 ? executionInfo.join(' | ') : '-';
          doc.text(fullText || '-', 26, yPos);
          
          yPos += 4;
        } else {
          yPos += 2;
        }

        yPos += 4; // Espaço antes da seção de OSs

        // Ordens de Serviço deste plano
        if (plan.work_orders.length > 0) {
          doc.setFontSize(8);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(60);
          doc.text(`Ordens de Servico (${plan.work_orders.length}):`, 26, yPos);
          yPos += 5;

          // Tabela de OSs com estilo aprimorado
          const osTableData = plan.work_orders.map((wo: any) => [
            wo.order_number ? formatWorkOrderNumber(wo.order_number) : wo.id?.slice(0, 8) || '-',
            translateStatus(wo.status),
            formatCurrency(wo.total_cost || 0),
            formatDate(wo.opened_at),
            wo.closed_at ? formatDate(wo.closed_at) : '-',
            wo.provider_name || '-'
          ]);

          autoTable(doc, {
            head: [['OS', 'Status', 'Custo', 'Abertura', 'Fechamento', 'Fornecedor']],
            body: osTableData,
            startY: yPos,
            margin: { left: 26, right: 14 },
            tableWidth: 170,
            theme: 'grid',
            headStyles: {
              fillColor: [79, 70, 229], // Roxo
              textColor: [255, 255, 255],
              fontSize: 8,
              fontStyle: 'bold',
              halign: 'center',
              cellPadding: 2
            },
            styles: {
              fontSize: 7,
              cellPadding: 2,
              lineColor: [220, 220, 220],
              lineWidth: 0.1
            },
            alternateRowStyles: {
              fillColor: [250, 250, 250]
            },
            columnStyles: {
              0: { cellWidth: 20, halign: 'center', fontStyle: 'bold' },
              1: { cellWidth: 28, halign: 'center' },
              2: { cellWidth: 24, halign: 'right', fontStyle: 'bold' },
              3: { cellWidth: 24, halign: 'center' },
              4: { cellWidth: 24, halign: 'center' },
              5: { cellWidth: 50 }
            },
            didParseCell: (data: any) => {
              // Colorir status
              if (data.column.index === 1 && data.section === 'body') {
                const status = plan.work_orders[data.row.index]?.status;
                if (status === 'completed' || status === 'closed') {
                  data.cell.styles.textColor = [40, 167, 69]; // Verde
                  data.cell.styles.fontStyle = 'bold';
                } else if (status === 'in_progress') {
                  data.cell.styles.textColor = [255, 152, 0]; // Laranja
                  data.cell.styles.fontStyle = 'bold';
                } else if (status === 'open') {
                  data.cell.styles.textColor = [0, 123, 255]; // Azul
                  data.cell.styles.fontStyle = 'bold';
                }
              }
            }
          });

          yPos = (doc as any).lastAutoTable.finalY + 6;
        } else {
          doc.setFontSize(7);
          doc.setFont('helvetica', 'italic');
          doc.setTextColor(150);
          doc.text('Sem ordens de servico registradas', 26, yPos);
          yPos += 6;
        }

        yPos += 4; // Espaço entre planos
      }

      // Separador entre veículos
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.3);
      doc.line(14, yPos, 196, yPos);
      yPos += 8;
    }
  };

  const generateMaintenancePDF = async (doc: jsPDF, config: ExportConfig) => {
    const { data, company } = config;

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Relatório de Manutenção', 14, 20);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Empresa: ${company?.name || 'N/A'}`, 14, 28);
    doc.text(`Gerado em: ${new Date().toLocaleString('pt-BR')}`, 14, 34);
    
    const tableData = data.map(m => [
      m.order_number || 'N/A',
      m.fleet_vehicles?.plate || 'N/A',
      formatDate(m.created_at),
      translateStatus(m.status),
      formatCurrency(m.total_cost || 0)
    ]);

    autoTable(doc, {
      head: [['N° OS', 'Veículo', 'Data', 'Status', 'Custo']],
      body: tableData,
      startY: 40,
      theme: 'grid',
      headStyles: { fillColor: [66, 139, 202] },
    });
  };

  const generateCostCentersPDF = async (doc: jsPDF, config: ExportConfig) => {
    const { data, filters, company } = config;

    const yPos = await addProfessionalHeader(doc, {
      reportType: 'cost-centers',
      reportTitle: 'Relatório de Centros de Custo',
      company,
      filters
    });
    
    const tableData = data.map(c => [
      c.code || 'N/A',
      c.name || 'N/A',
      c.description || '-',
      translateStatus(c.status)
    ]);

    autoTable(doc, {
      head: [['Código', 'Nome', 'Descrição', 'Status']],
      body: tableData,
      startY: yPos,
      ...getTableStyles(company),
    });
  };

  const generateFinancialPDF = async (doc: jsPDF, config: ExportConfig) => {
    const { data, filters, company } = config;

    const yPos = await addProfessionalHeader(doc, {
      reportType: 'financial',
      reportTitle: 'Relatório Financeiro',
      company,
      filters
    });
    
    autoTable(doc, {
      head: [['Descrição', 'Valor']],
      body: [['Total de Registros', data.length.toString()]],
      startY: yPos,
      ...getTableStyles(company),
    });
  };

  const generateOperationalPDF = async (doc: jsPDF, config: ExportConfig) => {
    const { data, filters, company } = config;

    const yPos = await addProfessionalHeader(doc, {
      reportType: 'operational',
      reportTitle: 'Relatório Operacional',
      company,
      filters
    });
    
    autoTable(doc, {
      head: [['Descrição', 'Valor']],
      body: [['Total de Registros', data.length.toString()]],
      startY: yPos,
      ...getTableStyles(company),
    });
  };

  const generateDocumentsPDF = async (doc: jsPDF, config: ExportConfig) => {
    const { data, filters, company } = config;

    let yPos = await addProfessionalHeader(doc, {
      reportType: 'documents',
      reportTitle: 'Relatório de Documentos',
      company,
      filters
    });

    const now = new Date();
    const validDocs = data.filter(d => {
      if (!d.expiry_date) return true;
      return new Date(d.expiry_date) >= now;
    });
    const expiredDocs = data.filter(d => {
      if (!d.expiry_date) return false;
      return new Date(d.expiry_date) < now;
    });

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Resumo Executivo', 14, yPos);
    yPos += 4;
    
    const kpis = [
      ['Total de Documentos', data.length.toString()],
      ['Documentos Válidos', validDocs.length.toString()],
      ['Documentos Vencidos', expiredDocs.length.toString()]
    ];

    autoTable(doc, {
      head: [['Indicador', 'Valor']],
      body: kpis,
      startY: yPos,
      theme: 'grid',
      headStyles: { fillColor: [66, 139, 202] as [number, number, number] },
      styles: { fontSize: 9 }
    });

    yPos = (doc as any).lastAutoTable.finalY + 10;

    const allDocsData = data.map(d => {
      const expiryDate = d.expiry_date ? new Date(d.expiry_date) : null;
      const isExpired = expiryDate && expiryDate < now;
      const status = !expiryDate ? 'Sem Validade' : isExpired ? 'VENCIDO' : 'Válido';
      
      return [
        d.document_type || 'N/A',
        d.fleet_vehicles?.plate || 'N/A',
        formatDate(d.expiry_date),
        status
      ];
    });

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Lista de Documentos', 14, yPos);

    autoTable(doc, {
      head: [['Tipo', 'Veículo', 'Vencimento', 'Status']],
      body: allDocsData,
      startY: yPos + 4,
      theme: 'striped',
      headStyles: { fillColor: [66, 139, 202] as [number, number, number] },
      styles: { fontSize: 8 },
    });
  };

  const generateTiresPDF = async (doc: jsPDF, config: ExportConfig) => {
    const { data, filters, company } = config;

    let yPos = await addProfessionalHeader(doc, {
      reportType: 'tires',
      reportTitle: 'Relatório de Pneus',
      company,
      filters
    });

    const byStatus = data.reduce((acc, tire) => {
      const status = tire.status || 'unknown';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const totalValue = data.reduce((sum, t) => sum + (t.purchase_price || 0), 0);

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Resumo Executivo', 14, yPos);
    yPos += 4;
    
    const kpis = [
      ['Total de Pneus', data.length.toString()],
      ['Instalados', (byStatus['installed'] || 0).toString()],
      ['Em Estoque', (byStatus['in_stock'] || 0).toString()],
      ['Valor Total', formatCurrency(totalValue)]
    ];

    autoTable(doc, {
      head: [['Indicador', 'Valor']],
      body: kpis,
      startY: yPos,
      theme: 'grid',
      headStyles: { fillColor: [66, 139, 202] as [number, number, number] },
      styles: { fontSize: 9 }
    });

    yPos = (doc as any).lastAutoTable.finalY + 10;

    const installedTires = data.filter(t => t.status === 'installed');
    if (installedTires.length > 0) {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(`Pneus Instalados (${installedTires.length})`, 14, yPos);
      
      const installedData = installedTires.map(t => [
        t.dot || 'N/A',
        t.brand || 'N/A',
        t.model || 'N/A',
        t.fleet_vehicles?.plate || 'N/A',
        t.position || 'N/A'
      ]);

      autoTable(doc, {
        head: [['DOT', 'Marca', 'Modelo', 'Veículo', 'Posição']],
        body: installedData,
        startY: yPos + 4,
        theme: 'grid',
        headStyles: { fillColor: [40, 167, 69] as [number, number, number] },
        styles: { fontSize: 8 }
      });
    }
  };

  // Helper: Traduzir tipo de combustível
  const translateFuelType = (type: string): string => {
    const typeMap: Record<string, string> = {
      'gasoline': 'Gasolina',
      'ethanol': 'Etanol',
      'diesel': 'Diesel',
      'gnv': 'GNV'
    };
    return typeMap[type] || type;
  };

  const generateFuelPDF = async (doc: jsPDF, config: ExportConfig) => {
    const { data, filters, company } = config;
    const primaryColor = hexToRgb(company?.branding?.primary_color || '#2563eb');
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    // ====== PÁGINA 1: CAPA EXECUTIVA ======
    let yPos = 50;
    
    const logoUrl = company?.branding?.logo || company?.logo_url;
    if (logoUrl) {
      try {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = logoUrl;
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          setTimeout(reject, 3000);
        });
        if (img.complete && img.naturalHeight !== 0) {
          doc.addImage(img, 'PNG', (pageWidth - 50) / 2, yPos, 50, 25);
          yPos += 35;
        }
      } catch (e) {}
    }

    doc.setFontSize(28);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.text('RELATORIO DE', pageWidth / 2, yPos + 20, { align: 'center' });
    doc.text('ABASTECIMENTOS', pageWidth / 2, yPos + 35, { align: 'center' });

    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text('Analise de Consumo e Custos de Combustivel', pageWidth / 2, yPos + 50, { align: 'center' });

    yPos += 65;
    doc.setDrawColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.setLineWidth(1);
    doc.line(40, yPos, pageWidth - 40, yPos);

    yPos += 20;
    doc.setFontSize(12);
    doc.setTextColor(60, 60, 60);
    doc.text(`Empresa: ${company?.name || 'N/A'}`, pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;
    doc.text(`Total de Abastecimentos: ${data.length}`, pageWidth / 2, yPos, { align: 'center' });

    const now = new Date();
    yPos += 10;
    doc.text(`Gerado em: ${now.toLocaleDateString('pt-BR')} as ${now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`, pageWidth / 2, yPos, { align: 'center' });

    // ====== PÁGINA 2: RESUMO EXECUTIVO ======
    doc.addPage();
    yPos = 25;

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.text('RESUMO EXECUTIVO', 14, yPos);
    doc.setDrawColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.setLineWidth(0.5);
    doc.line(14, yPos + 3, 100, yPos + 3);
    yPos += 20;

    // KPIs
    const totalGasto = data.reduce((sum, log) => sum + ((log.liters || 0) * (log.price_unit || 0)), 0);
    const totalLitros = data.reduce((sum, log) => sum + (log.liters || 0), 0);
    const precoMedio = data.length > 0 ? data.reduce((sum, log) => sum + (log.price_unit || 0), 0) / data.length : 0;
    const vehicleCount = new Set(data.map(l => l.fleet_vehicles?.plate).filter(Boolean)).size;

    const kpiCards = [
      { label: 'Total Gasto', value: formatCurrency(totalGasto), color: [245, 158, 11] as [number, number, number] },
      { label: 'Total Litros', value: `${totalLitros.toFixed(0)}L`, color: [16, 185, 129] as [number, number, number] },
      { label: 'Preco Medio/L', value: formatCurrency(precoMedio), color: [37, 99, 235] as [number, number, number] },
      { label: 'Abastecimentos', value: data.length.toString(), color: [139, 92, 246] as [number, number, number] },
      { label: 'Veiculos', value: vehicleCount.toString(), color: [59, 130, 246] as [number, number, number] },
      { label: 'Media/Abast.', value: `${(totalLitros / Math.max(data.length, 1)).toFixed(1)}L`, color: [16, 185, 129] as [number, number, number] }
    ];

    const cardWidth = 58;
    const cardHeight = 35;
    const cardSpacing = 5;
    const startX = 14;

    kpiCards.forEach((card, idx) => {
      const col = idx % 3;
      const row = Math.floor(idx / 3);
      const x = startX + col * (cardWidth + cardSpacing);
      const y = yPos + row * (cardHeight + cardSpacing);

      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.3);
      doc.rect(x, y, cardWidth, cardHeight, 'FD');
      doc.setFillColor(card.color[0], card.color[1], card.color[2]);
      doc.rect(x, y, cardWidth, 4, 'F');

      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text(card.label, x + cardWidth / 2, y + 12, { align: 'center' });

      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(40, 40, 40);
      doc.text(card.value, x + cardWidth / 2, y + 25, { align: 'center' });
    });

    yPos += 2 * (cardHeight + cardSpacing) + 15;

    // ====== PÁGINA 3: HISTÓRICO DETALHADO ======
    doc.addPage();
    yPos = 25;

    doc.setFillColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.rect(14, yPos - 5, 4, 8, 'F');
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.text('HISTORICO DE ABASTECIMENTOS', 20, yPos);
    doc.setDrawColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.setLineWidth(0.5);
    doc.line(20, yPos + 3, 196, yPos + 3);
    yPos += 10;

    autoTable(doc, {
      head: [['Data', 'Veiculo', 'Posto', 'Combustivel', 'Litros', 'R$/L', 'Total', 'KM']],
      body: data.map((log: any) => [
        formatDate(log.occurred_at),
        log.fleet_vehicles?.plate || 'N/A',
        log.fleet_providers?.name || 'N/A',
        translateFuelType(log.fuel_type || 'gasoline'),
        `${(log.liters || 0).toFixed(2)}L`,
        formatCurrency(log.price_unit || 0),
        formatCurrency((log.liters || 0) * (log.price_unit || 0)),
        formatDistance(log.odometer || 0)
      ]),
      startY: yPos,
      ...getTableStyles(company),
      styles: { fontSize: 8, cellPadding: 2.5 },
      columnStyles: {
        0: { cellWidth: 22 },
        1: { cellWidth: 20 },
        2: { cellWidth: 36 },
        3: { cellWidth: 22 },
        4: { cellWidth: 18, halign: 'right' },
        5: { cellWidth: 18, halign: 'right' },
        6: { cellWidth: 28, halign: 'right' },
        7: { cellWidth: 18, halign: 'right' }
      }
    });

    yPos = (doc as any).lastAutoTable.finalY + 15;

    // Resumo por veículo
    const vehicleMap = data.reduce((acc: any, log: any) => {
      const plate = log.fleet_vehicles?.plate || 'N/A';
      if (!acc[plate]) acc[plate] = { count: 0, liters: 0, cost: 0 };
      acc[plate].count++;
      acc[plate].liters += log.liters || 0;
      acc[plate].cost += (log.liters || 0) * (log.price_unit || 0);
      return acc;
    }, {});

    const vehicleSummary = Object.entries(vehicleMap).map(([plate, stats]: [string, any]) => ({
      plate, count: stats.count, liters: stats.liters, cost: stats.cost
    })).sort((a, b) => b.cost - a.cost);

    if (vehicleSummary.length > 1) {
      if (yPos > 220) { doc.addPage(); yPos = 25; }

      doc.setFillColor(primaryColor.r, primaryColor.g, primaryColor.b);
      doc.rect(14, yPos - 5, 4, 8, 'F');
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b);
      doc.text('RESUMO POR VEICULO', 20, yPos);
      doc.setDrawColor(primaryColor.r, primaryColor.g, primaryColor.b);
      doc.setLineWidth(0.5);
      doc.line(20, yPos + 3, 196, yPos + 3);
      yPos += 10;

      autoTable(doc, {
        head: [['Veiculo', 'Abastecimentos', 'Total Litros', 'Total Gasto', 'Media/Abast.']],
        body: vehicleSummary.map(v => [
          v.plate,
          v.count.toString(),
          `${v.liters.toFixed(0)}L`,
          formatCurrency(v.cost),
          formatCurrency(v.cost / v.count)
        ]),
        startY: yPos,
        ...getTableStyles(company),
        columnStyles: {
          0: { cellWidth: 38 },
          1: { cellWidth: 32, halign: 'center' },
          2: { cellWidth: 32, halign: 'right' },
          3: { cellWidth: 40, halign: 'right' },
          4: { cellWidth: 38, halign: 'right' }
        }
      });
    }

    // ====== ÚLTIMA PÁGINA: CONCLUSÃO ======
    doc.addPage();
    yPos = 25;

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.text('CONCLUSAO EXECUTIVA', 14, yPos);
    doc.setDrawColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.setLineWidth(0.5);
    doc.line(14, yPos + 3, 110, yPos + 3);
    yPos += 15;

    const mediaAbast = data.length > 0 ? totalLitros / data.length : 0;
    const summary = `Foram registrados ${data.length} abastecimentos totalizando ${totalLitros.toFixed(0)} litros e ${formatCurrency(totalGasto)}. A media por abastecimento foi de ${mediaAbast.toFixed(1)} litros.`;

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(40, 40, 40);
    const summaryLines = doc.splitTextToSize(summary, 175);
    doc.text(summaryLines, 14, yPos);
    yPos += summaryLines.length * 6 + 10;

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.text('Recomendacoes:', 14, yPos);
    yPos += 10;

    const recommendations = [
      'Monitorar veiculos com consumo acima da media da frota.',
      'Avaliar rotas e comportamento de conducao para otimizar consumo.',
      'Considerar negociacao com postos para melhores precos.',
      'Manter registro preciso do odometro em cada abastecimento.'
    ];

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(40, 40, 40);

    recommendations.forEach((rec, idx) => {
      doc.setFillColor(primaryColor.r, primaryColor.g, primaryColor.b);
      doc.circle(18, yPos - 1.5, 3, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8);
      doc.text(String(idx + 1), 18, yPos, { align: 'center' });
      doc.setTextColor(40, 40, 40);
      doc.setFontSize(10);
      const lines = doc.splitTextToSize(rec, 168);
      doc.text(lines, 26, yPos);
      yPos += lines.length * 5 + 4;
    });
  };

  // Função reutilizável para gerar relatório individual completo de um veículo
  const generateSingleVehicleReport = async (
    doc: jsPDF, 
    vehicleData: any, 
    config: ExportConfig,
    isFirstVehicle: boolean
  ) => {
    const { vehicle, work_orders = [], maintenance_plans = [] } = vehicleData;
    const { company, filters } = config;
    
    if (!vehicle) {
      throw new Error('Dados do veículo não encontrados');
    }

    // Add header apenas para o primeiro veículo
    let yPos = 20;
    if (isFirstVehicle) {
      yPos = await addProfessionalHeader(doc, {
        reportType: 'vehicle-maintenance-summary',
        reportTitle: 'Sumário de Manutenção por Veículo',
        company,
        filters
      });
    }

    const primaryColor = hexToRgb(company?.branding?.primary_color || '#2563eb');

    // VEHICLE INFORMATION SECTION
    doc.setFillColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.rect(14, yPos - 5, 4, 8, 'F');
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.text('INFORMAÇÕES DO VEÍCULO', 20, yPos);
    yPos += 3;

    doc.setDrawColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.setLineWidth(0.5);
    doc.line(20, yPos, 196, yPos);
    yPos += 8;

    autoTable(doc, {
      body: [
        ['Modelo', `${vehicle.make || ''} ${vehicle.model || ''}`, 'Placa', vehicle.plate || 'N/A'],
        ['Odômetro Atual', formatDistance(vehicle.odometer || 0), 'Status', translateStatus(vehicle.status)]
      ],
      startY: yPos,
      ...getTableStyles(company),
      theme: 'plain',
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 40 },
        1: { cellWidth: 50 },
        2: { fontStyle: 'bold', cellWidth: 40 },
        3: { cellWidth: 52 }
      }
    });

    yPos = (doc as any).lastAutoTable.finalY + 15;

    // 1. STATUS OPERACIONAL
    if (yPos > 240) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFillColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.rect(14, yPos - 5, 4, 8, 'F');
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.text('1. STATUS OPERACIONAL', 20, yPos);
    yPos += 3;

    doc.setDrawColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.setLineWidth(0.5);
    doc.line(20, yPos, 196, yPos);
    yPos += 8;

    const vehicleStatus = vehicle?.status || 'active';
    const statusLabel = translateStatus(vehicleStatus);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text(`Situação: ${statusLabel}`, 25, yPos);
    yPos += 6;
    
    // Se em manutenção, listar OSs abertas
    if (vehicleStatus === 'maintenance') {
      const openOrders = work_orders.filter((wo: any) => 
        wo.status === 'open' || wo.status === 'in_progress'
      );
      
      if (openOrders.length > 0) {
        openOrders.forEach((wo: any) => {
          if (yPos > 270) {
            doc.addPage();
            yPos = 20;
          }
          
          const description = wo.description || 'N/A';
          doc.text(`--- Motivo: ${description.substring(0, 50)}`, 30, yPos);
          yPos += 5;
          
          // Se tiver plano vinculado, mostrar nome do plano
          if (wo.fleet_maint_plans?.name) {
            doc.text(`    Plano: ${wo.fleet_maint_plans.name}`, 30, yPos);
            yPos += 5;
          }
        });
      }
    }

    yPos += 10;

    // 2. PLANOS DE MANUTENÇÃO
    if (maintenance_plans && maintenance_plans.length > 0) {
      if (yPos > 200) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFillColor(primaryColor.r, primaryColor.g, primaryColor.b);
      doc.rect(14, yPos - 5, 4, 8, 'F');
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b);
      doc.text('2. PLANOS DE MANUTENÇÃO', 20, yPos);
      yPos += 3;

      doc.setDrawColor(primaryColor.r, primaryColor.g, primaryColor.b);
      doc.setLineWidth(0.5);
      doc.line(20, yPos, 196, yPos);
      yPos += 8;

      const plansTableData = maintenance_plans.map((plan: any) => {
        // Remover emojis para compatibilidade com jsPDF
        const approvalLabel = plan.approval_status === 'pending' 
          ? 'Aguardando' 
          : plan.approval_status === 'approved' 
            ? 'Aprovado' 
            : plan.approval_status === 'rejected' 
              ? 'Rejeitado' 
              : '-';
        
        const statusLabel = plan.status === 'active' 
          ? 'Ativo' 
          : plan.status === 'inactive' 
            ? 'Inativo' 
            : 'Concluído';
        
        const typeLabel = translateMaintenanceType(plan.type);
        
        return [
          plan.name || 'N/A',
          typeLabel,
          statusLabel,
          approvalLabel,
          plan.next_execution_date
            ? formatDate(plan.next_execution_date)
            : plan.next_execution_km 
              ? `${plan.next_execution_km.toLocaleString('pt-BR')} km` 
              : '-'
        ];
      });
      
      autoTable(doc, {
        head: [['Plano', 'Tipo', 'Status', 'Aprovação', 'Próxima Execução']],
        body: plansTableData,
        startY: yPos,
        ...getTableStyles(company),
        columnStyles: {
          0: { cellWidth: 45 },
          1: { cellWidth: 35 },
          2: { cellWidth: 28, halign: 'center' },
          3: { cellWidth: 35, halign: 'center' },
          4: { cellWidth: 38, halign: 'center' }
        }
      });
      
      yPos = (doc as any).lastAutoTable.finalY + 15;
    } else {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(150, 150, 150);
      doc.text('Nenhum plano de manutenção registrado para este veículo.', 25, yPos);
      yPos += 15;
    }

    // 3. OCORRÊNCIAS TÉCNICAS POR CATEGORIA
    if (maintenance_plans.length > 0) {
      if (yPos > 220) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFillColor(primaryColor.r, primaryColor.g, primaryColor.b);
      doc.rect(14, yPos - 5, 4, 8, 'F');
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b);
      doc.text('3. OCORRÊNCIAS TÉCNICAS', 20, yPos);
      yPos += 3;

      doc.setDrawColor(primaryColor.r, primaryColor.g, primaryColor.b);
      doc.setLineWidth(0.5);
      doc.line(20, yPos, 196, yPos);
      yPos += 8;
      
      // Agrupar PLANOS por categoria técnica
      const categoryGroups: Record<string, any[]> = {};
      
      maintenance_plans.forEach((plan: any) => {
        const category = plan.maintenance_category || 'other';
        if (!categoryGroups[category]) categoryGroups[category] = [];
        categoryGroups[category].push(plan);
      });
      
      // Ordem das categorias
      const orderedCategories: MaintenanceCategory[] = [
        'mechanical', 'tire', 'suspension', 'electrical', 'bodywork', 'other'
      ];
      
      let sectionNumber = 1;
      orderedCategories.forEach((categoryId: MaintenanceCategory) => {
        const items = categoryGroups[categoryId];
        if (!items || items.length === 0) return;
        
        const categoryLabel = getCategoryLabel(categoryId);
        
        if (yPos > 260) {
          doc.addPage();
          yPos = 20;
        }
        
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(52, 73, 94);
        doc.text(`3.${sectionNumber} ${categoryLabel}`, 25, yPos);
        yPos += 6;
        
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);
        
        items.forEach((plan: any) => {
          if (yPos > 270) {
            doc.addPage();
            yPos = 20;
          }
          
          // Verificar se o plano tem OS vinculada
          const linkedWO = work_orders.find((wo: any) => wo.plan_id === plan.id);
          const hasWorkOrder = linkedWO ? '[X]' : '[ ]';
          
          const planName = plan.name || 'N/A';
          const truncatedName = planName.length > 60 ? planName.substring(0, 60) + '...' : planName;
          
          doc.text(`${hasWorkOrder} ${truncatedName}`, 30, yPos);
          yPos += 5;
        });
        
        yPos += 3;
        sectionNumber++;
      });

      // Legenda
      yPos += 2;
      doc.setFontSize(8);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(100, 100, 100);
      doc.text('[X] = Possui OS vinculada  |  [ ] = Plano sem OS', 25, yPos);
      yPos += 10;
    }

    // 4. MANUTENÇÕES REALIZADAS (apenas fechadas)
    const closedOrders = work_orders.filter((wo: any) => 
      wo.status === 'closed' || wo.status === 'completed'
    );
    
    if (closedOrders.length > 0) {
      if (yPos > 200) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFillColor(primaryColor.r, primaryColor.g, primaryColor.b);
      doc.rect(14, yPos - 5, 4, 8, 'F');
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b);
      doc.text('4. MANUTENÇÕES REALIZADAS', 20, yPos);
      yPos += 3;

      doc.setDrawColor(primaryColor.r, primaryColor.g, primaryColor.b);
      doc.setLineWidth(0.5);
      doc.line(20, yPos, 196, yPos);
      yPos += 8;

      const tableData = closedOrders.map((wo: any) => [
        formatWorkOrderNumber(wo.order_number) || wo.id?.slice(0, 8) || 'N/A',
        formatDate(wo.opened_at),
        wo.closed_at ? formatDate(wo.closed_at) : '-',
        (wo.description || wo.maintenance_type || '').substring(0, 30),
        wo.fleet_providers?.name || '-',
        translateStatus(wo.status),
        formatCurrency(wo.total_cost || 0)
      ]);

      autoTable(doc, {
        head: [['N° OS', 'Abertura', 'Fechamento', 'Descrição', 'Fornecedor', 'Status', 'Valor']],
        body: tableData,
        startY: yPos,
        ...getTableStyles(company),
        styles: {
          fontSize: 8,
          cellPadding: 2
        },
        columnStyles: {
          0: { cellWidth: 22, halign: 'center' },
          1: { cellWidth: 22, halign: 'center' },
          2: { cellWidth: 22, halign: 'center' },
          3: { cellWidth: 42 },
          4: { cellWidth: 30 },
          5: { cellWidth: 22, halign: 'center' },
          6: { cellWidth: 22, halign: 'right' }
        }
      });

      yPos = (doc as any).lastAutoTable.finalY + 10;

      // TOTAL COSTS (apenas das fechadas)
      const totalCost = closedOrders.reduce((sum: number, wo: any) => sum + (wo.total_cost || 0), 0);

      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b);
      doc.text('Total de Custos:', 124, yPos);
      doc.text(formatCurrency(totalCost), 175, yPos, { align: 'right' });
    } else if (work_orders.length > 0) {
      // Tem OSs mas nenhuma fechada
      doc.setFontSize(10);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(100, 100, 100);
      doc.text('Nenhuma manutenção foi concluída ainda.', 105, yPos, { align: 'center' });
    } else {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(100, 100, 100);
      doc.text('Nenhuma manutenção encontrada para este veículo no período selecionado.', 105, yPos, { align: 'center' });
    }
  };

  const generateVehicleMaintenanceSummaryPDF = async (doc: jsPDF, config: ExportConfig) => {
    const { data } = config;
    
    // Detectar formato dos dados (único veículo ou múltiplos)
    const isMultiVehicle = !Array.isArray(data) && (data as any).vehicles && Array.isArray((data as any).vehicles);
    
    if (isMultiVehicle) {
      // MÚLTIPLOS VEÍCULOS - Gerar relatório completo individual para cada um
      const vehicles = (data as any).vehicles;
      
      if (vehicles.length === 0) {
        throw new Error('Nenhum veículo encontrado para gerar o relatório');
      }
      
      // Iterar sobre cada veículo e gerar seu relatório completo
      for (let i = 0; i < vehicles.length; i++) {
        const vehicleData = vehicles[i];
        
        // Gerar relatório completo individual usando a função reutilizável
        await generateSingleVehicleReport(doc, vehicleData, config, i === 0);
        
        // Adicionar nova página entre veículos (exceto no último)
        if (i < vehicles.length - 1) {
          doc.addPage();
        }
      }
    } else {
      // VEÍCULO ÚNICO - Usar lógica padrão
      const reportData = Array.isArray(data) ? data[0] : data;
      await generateSingleVehicleReport(doc, reportData, config, true);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDistance = (value: number) => {
    return `${(value || 0).toLocaleString('pt-BR')} km`;
  };

  const formatDate = (date: string | null | undefined) => {
    if (!date) return 'Data não Especificada';
    try {
      const parsedDate = new Date(date);
      if (isNaN(parsedDate.getTime())) return 'Data não Especificada';
      return parsedDate.toLocaleDateString('pt-BR');
    } catch {
      return 'Data não Especificada';
    }
  };

  const translateStatus = (status: string) => {
    const statusMap: Record<string, string> = {
      'open': 'Em Andamento',
      'closed': 'Concluído',
      'pending': 'Pendente',
      'in_progress': 'Em Andamento',
      'cancelled': 'Cancelado',
      'planned': 'Planejado',
      'active': 'Ativo',
      'completed': 'Concluído'
    };
    return statusMap[status?.toLowerCase()] || status || 'N/A';
  };

  const translateCategory = (category: string) => {
    const categoryMap: Record<string, string> = {
      'car': 'Carro',
      'truck': 'Caminhão',
      'van': 'Van',
      'motorcycle': 'Motocicleta',
      'bus': 'Ônibus',
      'suv': 'SUV',
      'pickup': 'Caminhonete',
      'utility': 'Utilitário'
    };
    return categoryMap[category?.toLowerCase()] || category || 'N/A';
  };

  const getReportConfig = (type: string) => {
    const configs = {
      vehicles: {
        title: 'Relatório de Veículos',
        headers: ['Veículo', 'Placa', 'Viagens', 'Distância', 'Combustível', 'Manutenção', 'Eficiência'],
        dataKeys: ['vehicle_model', 'vehicle_plate', 'total_trips', 'total_distance', 'total_fuel_cost', 'total_maintenance_cost', 'fuel_efficiency']
      },
      drivers: {
        title: 'Relatório de Motoristas',
        headers: ['Motorista', 'CNH', 'Viagens', 'Distância', 'Eficiência', 'Segurança', 'Violações'],
        dataKeys: ['name', 'license_number', 'trip_count', 'total_distance', 'fuel_efficiency', 'safety_score', 'violations_count']
      },
      maintenance: {
        title: 'Relatório de Manutenção',
        headers: ['N° OS', 'Data Abertura', 'Veículo', 'Tipo', 'Fornecedor', 'Status', 'Custo'],
        dataKeys: ['order_number', 'opened_at', 'vehicle_plate', 'maintenance_type', 'provider_name', 'status', 'cost']
      },
      'cost-centers': {
        title: 'Relatório de Centros de Custo',
        headers: ['Centro de Custo', 'Veículos', 'Motoristas', 'Custo Total', 'Combustível', 'Manutenção', 'Custo/Km'],
        dataKeys: ['name', 'vehicle_count', 'driver_count', 'total_cost', 'fuel_cost', 'maintenance_cost', 'cost_per_km']
      },
      financial: {
        title: 'Relatório Financeiro',
        headers: ['Categoria', 'Valor', 'Percentual', 'Variação'],
        dataKeys: ['category', 'amount', 'percentage', 'change']
      },
      operational: {
        title: 'Relatório Operacional',
        headers: ['Métrica', 'Valor', 'Meta', 'Status'],
        dataKeys: ['metric', 'value', 'target', 'status']
      }
    };
    return configs[type as keyof typeof configs];
  };

  const exportToExcel = async (config: ExportConfig) => {
    // Handle vehicle detail report first (before data validation)
    if (config.type === 'vehicle-detail' && config.vehicleDetailData) {
      return exportVehicleDetailExcel(config.vehicleDetailData, config.filename);
    }

    // Validate data
    if (!config.data || config.data.length === 0) {
      throw new Error('Nenhum dado disponível para exportação');
    }

    const reportConfig = getReportConfig(config.type);
    if (!reportConfig) {
      throw new Error(`Tipo de relatório '${config.type}' não suportado`);
    }

    const workbook = XLSX.utils.book_new();
    
    // Resumo
    const summaryData = [
      ['Relatório', reportConfig.title],
      ['Data de Geração', new Date().toLocaleDateString('pt-BR')],
      ['Total de Registros', config.data.length.toString()],
      [''],
    ];

    // Dados principais
    const mainData = [
      reportConfig.headers,
      ...config.data.map(item => 
        reportConfig.dataKeys.map(key => {
          const value = item[key];
          if (key === 'status') {
            return translateStatus(value);
          }
          if (key === 'order_number' && config.type === 'maintenance') {
            return value ? formatWorkOrderNumber(value) : (item.id?.slice(0, 8) || 'N/A');
          }
          if (key === 'fuel_efficiency') {
            return value > 0 ? `${value.toFixed(1)} km/L` : 'N/A';
          }
          if (key.includes('cost') || key.includes('price')) {
            return formatCurrency(value);
          }
          if (key.includes('distance')) {
            return formatDistance(value);
          }
          if (key.includes('_date') || key.includes('_at') || key === 'date') {
            return formatDate(value);
          }
          return value?.toString() || '';
        })
      )
    ];

    const combinedData = [...summaryData, ...mainData];
    const worksheet = XLSX.utils.aoa_to_sheet(combinedData);
    
    // Ajustar largura das colunas
    const colWidths = reportConfig.headers.map(() => ({ wch: 15 }));
    worksheet['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Relatório');
    
    const filename = config.filename || `${config.type}-${Date.now()}.xlsx`;
    XLSX.writeFile(workbook, filename);
  };

  const exportVehicleDetailExcel = (data: VehicleDetailData, filename?: string) => {
    const { vehicle, kpis, workOrders, fuelLogs, trips, costHistory } = data;
    
    if (!vehicle) return;
    
    const workbook = XLSX.utils.book_new();

    // Summary Sheet
    const summaryData = [
      ['Relatório Detalhado do Veículo'],
      [''],
      ['Placa', vehicle.plate],
      ['Modelo', vehicle.model || 'N/A'],
      ['Marca', vehicle.make || 'N/A'],
      ['Ano', vehicle.year || 'N/A'],
      ['Status', vehicle.status],
      [''],
      ['INDICADORES'],
      ['Km Rodados', kpis.total_distance],
      ['Consumo Médio (km/L)', kpis.avg_fuel_efficiency],
      ['Custo Total', kpis.total_cost],
      ['Disponibilidade (%)', kpis.availability_pct],
    ];
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    summarySheet['!cols'] = [{ wch: 20 }, { wch: 20 }];
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Resumo');

    // Work Orders Sheet
    if (workOrders.length > 0) {
      const woData = workOrders.map(wo => ({
        'N° OS': wo.order_number ? formatWorkOrderNumber(wo.order_number) : wo.id.slice(0, 8),
        'Descrição': wo.description || 'N/A',
        'Fornecedor': wo.provider_name || 'Sem fornecedor',
        'Data Abertura': new Date(wo.opened_at).toLocaleDateString('pt-BR'),
        'Status': wo.status === 'closed' ? 'Concluído' : 'Aberto',
        'Custo': wo.total_cost || 0
      }));
      const woSheet = XLSX.utils.json_to_sheet(woData);
      woSheet['!cols'] = [{ wch: 15 }, { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 12 }, { wch: 12 }];
      XLSX.utils.book_append_sheet(workbook, woSheet, 'Manutenções');
    }

    // Fuel Logs Sheet
    if (fuelLogs.length > 0) {
      const fuelData = fuelLogs.map(log => ({
        'Data': new Date(log.occurred_at).toLocaleDateString('pt-BR'),
        'Litros': log.liters,
        'Preço/Litro': log.price_unit,
        'Valor Total': log.price_unit * log.liters,
        'Posto': log.station_name || 'Não informado',
        'Odômetro': log.odometer || 'N/A'
      }));
      const fuelSheet = XLSX.utils.json_to_sheet(fuelData);
      fuelSheet['!cols'] = [{ wch: 12 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 20 }, { wch: 12 }];
      XLSX.utils.book_append_sheet(workbook, fuelSheet, 'Abastecimentos');
    }

    // Trips Sheet
    if (trips.length > 0) {
      const tripData = trips.map(trip => ({
        'Data Início': trip.start_at ? new Date(trip.start_at).toLocaleDateString('pt-BR') : 'N/A',
        'Data Fim': trip.end_at ? new Date(trip.end_at).toLocaleDateString('pt-BR') : 'N/A',
        'Origem': trip.origin || 'N/A',
        'Destino': trip.destination || 'N/A',
        'Motorista': trip.driver_name || 'N/A',
        'Distância (km)': trip.distance_actual_km || 0,
        'Status': trip.status
      }));
      const tripSheet = XLSX.utils.json_to_sheet(tripData);
      tripSheet['!cols'] = [{ wch: 12 }, { wch: 12 }, { wch: 15 }, { wch: 15 }, { wch: 20 }, { wch: 12 }, { wch: 12 }];
      XLSX.utils.book_append_sheet(workbook, tripSheet, 'Viagens');
    }

    // Monthly Costs Sheet
    if (costHistory.length > 0) {
      const costData = costHistory.map(month => ({
        'Mês': month.month,
        'Combustível': month.fuel,
        'Manutenção': month.maintenance,
        'Total': month.total
      }));
      const costSheet = XLSX.utils.json_to_sheet(costData);
      costSheet['!cols'] = [{ wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 12 }];
      XLSX.utils.book_append_sheet(workbook, costSheet, 'Custos Mensais');
    }

    // Performance Sheet - NEW
    if (data.enrichedData?.performanceMetrics && data.enrichedData.performanceMetrics.length > 0) {
      const perfData = data.enrichedData.performanceMetrics.map(m => ({
        'Métrica': m.label,
        'Veículo': `${m.vehicleValue.toFixed(2)} ${m.unit}`,
        'Média Frota': `${m.fleetAverage.toFixed(2)} ${m.unit}`,
        'Status': m.trend === 'better' ? '✓ Melhor' : m.trend === 'worse' ? '✗ Pior' : '= Igual'
      }));
      
      const perfSheet = XLSX.utils.json_to_sheet(perfData);
      perfSheet['!cols'] = [{ wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 12 }];
      XLSX.utils.book_append_sheet(workbook, perfSheet, 'Performance');
    }

    // Timeline Sheet - NEW
    if (data.enrichedData?.timelineEvents && data.enrichedData.timelineEvents.length > 0) {
      const typeMap: Record<string, string> = {
        maintenance: 'Manutenção',
        fuel: 'Abastecimento',
        trip: 'Viagem',
        alert: 'Alerta'
      };

      const timelineData = data.enrichedData.timelineEvents.map(evt => ({
        'Data': new Date(evt.date).toLocaleDateString('pt-BR'),
        'Tipo': typeMap[evt.type] || evt.type,
        'Evento': evt.title,
        'Descrição': evt.description,
        'Custo': evt.cost || 0
      }));
      
      const timelineSheet = XLSX.utils.json_to_sheet(timelineData);
      timelineSheet['!cols'] = [{ wch: 12 }, { wch: 15 }, { wch: 25 }, { wch: 40 }, { wch: 12 }];
      XLSX.utils.book_append_sheet(workbook, timelineSheet, 'Timeline');
    }

    // Efficiency Sheet - NEW
    if (data.enrichedData?.efficiencyTrend && data.enrichedData.efficiencyTrend.length > 0) {
      const effData = data.enrichedData.efficiencyTrend.map(e => ({
        'Período': e.date,
        'Consumo (km/L)': e.kmPerLiter.toFixed(2),
        'Custo/km': e.costPerKm.toFixed(2),
        'Média Móvel': e.movingAverage?.toFixed(2) || 'N/A'
      }));
      
      const effSheet = XLSX.utils.json_to_sheet(effData);
      effSheet['!cols'] = [{ wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(workbook, effSheet, 'Eficiência');
    }

    // Predictions Sheet - NEW
    if (data.enrichedData?.maintenancePredictions && data.enrichedData.maintenancePredictions.length > 0) {
      const predData = data.enrichedData.maintenancePredictions.map(p => ({
        'Manutenção': p.planName,
        'Data Prevista': p.estimatedDate.toLocaleDateString('pt-BR'),
        'Km Previsto': p.estimatedKm,
        'Dias Restantes': p.isOverdue ? `ATRASADA (${p.daysUntil}d)` : p.daysUntil,
        'Km Restantes': p.kmUntil,
        'Confiança': `${p.confidence}%`
      }));
      
      const predSheet = XLSX.utils.json_to_sheet(predData);
      predSheet['!cols'] = [{ wch: 25 }, { wch: 15 }, { wch: 12 }, { wch: 18 }, { wch: 15 }, { wch: 12 }];
      XLSX.utils.book_append_sheet(workbook, predSheet, 'Previsões');
    }

    // Recommendations Sheet - NEW
    if (data.enrichedData?.recommendations && data.enrichedData.recommendations.length > 0) {
      const recData = data.enrichedData.recommendations.map(r => ({
        'Prioridade': r.priority.toUpperCase(),
        'Categoria': r.category,
        'Título': r.title,
        'Descrição': r.description,
        'Ação': r.action,
        'Economia Potencial': r.potentialSavings || 0
      }));
      
      const recSheet = XLSX.utils.json_to_sheet(recData);
      recSheet['!cols'] = [{ wch: 12 }, { wch: 15 }, { wch: 30 }, { wch: 50 }, { wch: 20 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(workbook, recSheet, 'Recomendações');
    }

    const fname = filename || `relatorio-veiculo-${vehicle.plate}-${Date.now()}.xlsx`;
    XLSX.writeFile(workbook, fname);
  };

  const exportToCSV = async (config: ExportConfig) => {
    // Validate data
    if (!config.data || config.data.length === 0) {
      throw new Error('Nenhum dado disponível para exportação');
    }

    const reportConfig = getReportConfig(config.type);
    if (!reportConfig) {
      throw new Error(`Tipo de relatório '${config.type}' não suportado`);
    }

    const csvData = [
      reportConfig.headers,
      ...config.data.map(item => 
        reportConfig.dataKeys.map(key => {
          const value = item[key];
          if (key === 'status') {
            return translateStatus(value);
          }
          if (key === 'order_number' && config.type === 'maintenance') {
            return value ? formatWorkOrderNumber(value) : (item.id?.slice(0, 8) || 'N/A');
          }
          if (key === 'fuel_efficiency') {
            return value > 0 ? `${value.toFixed(1)} km/L` : 'N/A';
          }
          if (key.includes('cost') || key.includes('price')) {
            return formatCurrency(value);
          }
          if (key.includes('distance')) {
            return formatDistance(value);
          }
          if (key.includes('date') || key.includes('at')) {
            return formatDate(value);
          }
          return value?.toString() || '';
        })
      )
    ];

    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const filename = config.filename || `${config.type}-${Date.now()}.csv`;
    
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  const exportToPDF = async (config: ExportConfig) => {
    // Handle vehicle detail report first (before data validation)
    if (config.type === 'vehicle-detail' && config.vehicleDetailData) {
      return exportVehicleDetailPDF(config.vehicleDetailData, config.filename);
    }

    // Validate data
    if (!config.data || config.data.length === 0) {
      throw new Error('Nenhum dado disponível para exportação');
    }

    // Handle maintenance report with analytics
    if (config.type === 'maintenance' && config.maintenanceAnalytics) {
      return exportMaintenanceReportPDF(config.data, config.maintenanceAnalytics, config.filters, config.filename);
    }

    const reportConfig = getReportConfig(config.type);
    if (!reportConfig) {
      throw new Error(`Tipo de relatório '${config.type}' não suportado`);
    }

    const doc = new jsPDF();
    
    // Cabeçalho profissional com FleetONE
    doc.setFillColor(59, 130, 246); // Azul FleetONE
    doc.rect(0, 0, 210, 35, 'F');

    // Logo e título no header
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('FleetONE', 20, 18);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Sistema de Gestão de Frotas', 20, 26);

    // Info da empresa (direita)
    doc.setFontSize(9);
    doc.text(config.company?.name || 'Empresa', 190, 18, { align: 'right' });
    doc.text(`Gerado em: ${new Date().toLocaleDateString('pt-BR')}`, 190, 24, { align: 'right' });

    // Linha decorativa
    doc.setDrawColor(59, 130, 246);
    doc.setLineWidth(2);
    doc.line(20, 38, 190, 38);

    // Seção de resumo
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(reportConfig.title, 20, 50);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Total de registros: ${config.data.length}`, 20, 58);

    let startY = 70;

    // Cards de resumo para manutenção
    if (config.type === 'maintenance' && config.data.length > 0) {
      const totalCost = config.data.reduce((sum, item) => sum + (item.cost || 0), 0);
      const completed = config.data.filter(item => item.status === 'closed').length;
      
      doc.setFillColor(240, 249, 255);
      doc.rect(20, 65, 85, 25, 'F');
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text('Custo Total', 25, 73);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text(formatCurrency(totalCost), 25, 82);
      
      doc.setFillColor(240, 253, 244);
      doc.rect(108, 65, 85, 25, 'F');
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text('OS Concluídas', 113, 73);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text(`${completed} / ${config.data.length}`, 113, 82);

      startY = 98;
    }

    // Tabela com dados
    const tableData = config.data.map(item => 
      reportConfig.dataKeys.map(key => {
        const value = item[key];
        if (key === 'status') {
          return translateStatus(value);
        }
        if (key === 'order_number' && config.type === 'maintenance') {
          return value ? formatWorkOrderNumber(value) : (item.id?.slice(0, 8) || 'N/A');
        }
        if (key === 'fuel_efficiency') {
          return value > 0 ? `${value.toFixed(1)} km/L` : 'N/A';
        }
        if (key.includes('cost') || key.includes('price')) {
          return formatCurrency(value);
        }
        if (key.includes('distance')) {
          return formatDistance(value);
        }
        if (key.includes('_date') || key.includes('_at') || key === 'date') {
          return formatDate(value);
        }
        return value?.toString() || '';
      })
    );

    autoTable(doc, {
      head: [reportConfig.headers],
      body: tableData,
      startY: startY,
      styles: {
        fontSize: 9,
        cellPadding: 3,
        lineColor: [220, 220, 220],
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [59, 130, 246],
        textColor: 255,
        fontSize: 10,
        fontStyle: 'bold',
        halign: 'left',
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252],
      },
      columnStyles: {
        ...(config.type === 'maintenance' && {
          6: { halign: 'right', fontStyle: 'bold' } // Custo
        })
      }
    });

    // Rodapé profissional
    const pageCount = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      
      // Linha decorativa no rodapé
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.5);
      doc.line(20, doc.internal.pageSize.height - 20, 190, doc.internal.pageSize.height - 20);
      
      // Texto do rodapé
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(
        'FleetONE - Sistema de Gestão de Frotas',
        20,
        doc.internal.pageSize.height - 12
      );
      doc.text(
        `Página ${i} de ${pageCount}`,
        doc.internal.pageSize.width / 2,
        doc.internal.pageSize.height - 12,
        { align: 'center' }
      );
      doc.text(
        new Date().toLocaleDateString('pt-BR'),
        190,
        doc.internal.pageSize.height - 12,
        { align: 'right' }
      );
    }

    const filename = config.filename || `${config.type}-${Date.now()}.pdf`;
    doc.save(filename);
  };

  const exportVehicleDetailPDF = (data: VehicleDetailData, filename?: string) => {
    const doc = new jsPDF();
    const { vehicle, kpis, workOrders, fuelLogs, trips, alerts, costHistory } = data;
    
    if (!vehicle) return;
    
    let yPos = 20;

    // Helper: Professional Footer
    const addFooter = (pageNum: number, totalPages: number) => {
      const pageHeight = doc.internal.pageSize.height;
      doc.setDrawColor(59, 130, 246);
      doc.setLineWidth(0.5);
      doc.line(15, pageHeight - 15, 195, pageHeight - 15);
      
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.setFont('helvetica', 'normal');
      doc.text('FleetONE - Sistema de Gestão de Frotas', 15, pageHeight - 10);
      doc.text(`Página ${pageNum} de ${totalPages}`, 195, pageHeight - 10, { align: 'right' });
      doc.text(`Gerado em: ${new Date().toLocaleDateString('pt-BR')}`, 105, pageHeight - 10, { align: 'center' });
    };

    // Helper: Section Header
    const addSectionHeader = (title: string, currentY: number): number => {
      doc.setDrawColor(59, 130, 246);
      doc.setFillColor(59, 130, 246);
      doc.rect(15, currentY, 3, 8, 'F');
      
      doc.setFillColor(245, 245, 245);
      doc.rect(18, currentY, 177, 8, 'F');
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(40, 40, 40);
      doc.text(title, 22, currentY + 6);
      
      return currentY + 12;
    };

    // Helper: Section Separator
    const addSectionSeparator = (currentY: number): number => {
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.3);
      doc.line(15, currentY, 195, currentY);
      return currentY + 8;
    };

    // Table Config for consistency
    const tableConfig = {
      styles: {
        fontSize: 9,
        cellPadding: 3,
        lineColor: [220, 220, 220] as [number, number, number],
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [59, 130, 246] as [number, number, number],
        textColor: 255,
        fontSize: 10,
        fontStyle: 'bold' as const,
        halign: 'left' as const,
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252] as [number, number, number],
      },
    };

    // Corporate Header
    doc.setFillColor(59, 130, 246);
    doc.rect(0, 0, 210, 35, 'F');
    
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('FleetONE', 15, 15);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Relatório Detalhado de Veículo', 15, 22);
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(`Placa: ${vehicle.plate || 'N/A'}`, 195, 20, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(`Gerado em: ${new Date().toLocaleString('pt-BR')}`, 195, 26, { align: 'right' });
    
    doc.setDrawColor(255, 200, 0);
    doc.setLineWidth(2);
    doc.line(15, 33, 195, 33);
    
    yPos = 45;
    doc.setTextColor(40, 40, 40);

    // Visual KPI Cards Section
    const cardWidth = 42;
    const cardHeight = 22;
    const cardSpacing = 4;
    const cardStartX = 15;

    const kpiCards = [
      { label: 'Km Rodados', value: formatDistance(kpis.total_distance), color: [59, 130, 246] },
      { label: 'Consumo Médio', value: `${kpis.avg_fuel_efficiency.toFixed(1)} km/L`, color: [16, 185, 129] },
      { label: 'Custo Total', value: formatCurrency(kpis.total_cost), color: [245, 158, 11] },
      { label: 'Disponibilidade', value: `${kpis.availability_pct.toFixed(1)}%`, color: [139, 92, 246] }
    ];

    kpiCards.forEach((card, idx) => {
      const x = cardStartX + (idx * (cardWidth + cardSpacing));
      
      // Card shadow
      doc.setFillColor(200, 200, 200);
      doc.rect(x + 0.5, yPos + 0.5, cardWidth, cardHeight, 'F');
      
      // Card background
      doc.setFillColor(255, 255, 255);
      doc.rect(x, yPos, cardWidth, cardHeight, 'F');
      
      // Card border
      doc.setDrawColor(card.color[0], card.color[1], card.color[2]);
      doc.setLineWidth(0.5);
      doc.rect(x, yPos, cardWidth, cardHeight);
      
      // Top accent bar
      doc.setFillColor(card.color[0], card.color[1], card.color[2]);
      doc.rect(x, yPos, cardWidth, 3, 'F');
      
      // Label
      doc.setFontSize(7);
      doc.setTextColor(100, 100, 100);
      doc.setFont('helvetica', 'normal');
      doc.text(card.label, x + cardWidth / 2, yPos + 8, { align: 'center' });
      
      // Value
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(40, 40, 40);
      doc.text(card.value, x + cardWidth / 2, yPos + 16, { align: 'center' });
    });

    yPos += cardHeight + 15;

    // Work Orders Section
    if (workOrders.length > 0) {
      yPos = addSectionHeader('Histórico de Manutenções', yPos);

      const woData = workOrders.slice(0, 10).map(wo => [
        wo.order_number ? formatWorkOrderNumber(wo.order_number) : wo.id.slice(0, 8),
        wo.description || 'N/A',
        wo.provider_name || 'Sem fornecedor',
        wo.status === 'closed' ? 'Concluído' : 'Aberto',
        formatCurrency(wo.total_cost || 0)
      ]);

      autoTable(doc, {
        head: [['N° OS', 'Tipo', 'Fornecedor', 'Status', 'Custo']],
        body: woData,
        startY: yPos,
        ...tableConfig,
      });

      yPos = (doc as any).lastAutoTable.finalY + 10;
      yPos = addSectionSeparator(yPos);
    }

    // Add new page if needed
    if (yPos > 240) {
      doc.addPage();
      yPos = 20;
    }

    // Fuel Logs Section
    if (fuelLogs.length > 0) {
      yPos = addSectionHeader('Histórico de Abastecimentos', yPos);

      const fuelData = fuelLogs.slice(0, 10).map(log => [
        new Date(log.occurred_at).toLocaleDateString('pt-BR'),
        `${log.liters.toFixed(1)} L`,
        formatCurrency(log.price_unit * log.liters),
        log.station_name || 'Não informado'
      ]);

      autoTable(doc, {
        head: [['Data', 'Litros', 'Valor', 'Posto']],
        body: fuelData,
        startY: yPos,
        ...tableConfig,
      });

      yPos = (doc as any).lastAutoTable.finalY + 10;
      yPos = addSectionSeparator(yPos);
    }

    // Performance vs Fleet Section - NEW
    if (data.enrichedData?.performanceMetrics && data.enrichedData.performanceMetrics.length > 0) {
      if (yPos > 240) {
        doc.addPage();
        yPos = 20;
      }
      
      yPos = addSectionHeader('Performance vs. Média da Frota', yPos);

      const perfData = data.enrichedData.performanceMetrics.map(m => {
        const diff = m.vehicleValue - m.fleetAverage;
        const better = m.inverse ? diff <= 0 : diff >= 0;
        const icon = better ? '✓' : '✗';
        
        return [
          m.label,
          `${m.vehicleValue.toFixed(1)} ${m.unit}`,
          `${m.fleetAverage.toFixed(1)} ${m.unit}`,
          icon
        ];
      });

      autoTable(doc, {
        head: [['Métrica', 'Veículo', 'Frota', 'Status']],
        body: perfData,
        startY: yPos,
        ...tableConfig,
      });

      yPos = (doc as any).lastAutoTable.finalY + 10;
      yPos = addSectionSeparator(yPos);
    }

    // Timeline Section - NEW
    if (data.enrichedData?.timelineEvents && data.enrichedData.timelineEvents.length > 0) {
      if (yPos > 240) {
        doc.addPage();
        yPos = 20;
      }
      
      yPos = addSectionHeader('Timeline de Eventos (Últimos 10)', yPos);

      const typeMap: Record<string, string> = {
        maintenance: 'Manutenção',
        fuel: 'Abastecimento',
        trip: 'Viagem',
        alert: 'Alerta'
      };

      const timelineData = data.enrichedData.timelineEvents.slice(0, 10).map(evt => [
        new Date(evt.date).toLocaleDateString('pt-BR'),
        typeMap[evt.type] || evt.type,
        evt.title.substring(0, 35) + (evt.title.length > 35 ? '...' : ''),
        evt.cost ? formatCurrency(evt.cost) : '-'
      ]);

      autoTable(doc, {
        head: [['Data', 'Tipo', 'Evento', 'Custo']],
        body: timelineData,
        startY: yPos,
        ...tableConfig,
        columnStyles: {
          2: { cellWidth: 70 }
        }
      });

      yPos = (doc as any).lastAutoTable.finalY + 10;
      yPos = addSectionSeparator(yPos);
    }

    // Efficiency Analysis Section - NEW
    if (data.enrichedData?.efficiencyTrend && data.enrichedData.efficiencyTrend.length > 0) {
      if (yPos > 240) {
        doc.addPage();
        yPos = 20;
      }
      
      yPos = addSectionHeader('Evolução do Consumo (Últimos 6 Meses)', yPos);

      const effData = data.enrichedData.efficiencyTrend.slice(-6).map(e => [
        e.date,
        `${e.kmPerLiter.toFixed(2)} km/L`,
        formatCurrency(e.costPerKm),
        e.movingAverage ? `${e.movingAverage.toFixed(2)} km/L` : '-'
      ]);

      autoTable(doc, {
        head: [['Período', 'Consumo', 'Custo/km', 'Média Móvel']],
        body: effData,
        startY: yPos,
        ...tableConfig,
      });

      yPos = (doc as any).lastAutoTable.finalY + 10;
      yPos = addSectionSeparator(yPos);
    }

    // Maintenance Predictions Section - NEW
    if (data.enrichedData?.maintenancePredictions && data.enrichedData.maintenancePredictions.length > 0) {
      if (yPos > 240) {
        doc.addPage();
        yPos = 20;
      }
      
      yPos = addSectionHeader('Próximas Manutenções Previstas', yPos);

      const predData = data.enrichedData.maintenancePredictions.map(p => [
        p.planName,
        p.isOverdue ? 'ATRASADA' : `${p.daysUntil}d`,
        `${p.kmUntil} km`,
        `${p.confidence}%`
      ]);

      autoTable(doc, {
        head: [['Manutenção', 'Prazo', 'Km Restantes', 'Confiança']],
        body: predData,
        startY: yPos,
        ...tableConfig,
      });

      yPos = (doc as any).lastAutoTable.finalY + 10;
      yPos = addSectionSeparator(yPos);
    }

    // Recommendations Section - NEW
    if (data.enrichedData?.recommendations && data.enrichedData.recommendations.length > 0) {
      if (yPos > 240) {
        doc.addPage();
        yPos = 20;
      }
      
      yPos = addSectionHeader('Recomendações', yPos);

      const recData = data.enrichedData.recommendations.map(r => [
        r.priority === 'high' ? '🔴' : r.priority === 'medium' ? '🟡' : '🟢',
        r.title.substring(0, 30) + (r.title.length > 30 ? '...' : ''),
        r.description.substring(0, 50) + (r.description.length > 50 ? '...' : ''),
        r.potentialSavings ? formatCurrency(r.potentialSavings) : '-'
      ]);

      autoTable(doc, {
        head: [['!', 'Ação', 'Descrição', 'Economia']],
        body: recData,
        startY: yPos,
        ...tableConfig,
        columnStyles: {
          0: { cellWidth: 10 },
          2: { cellWidth: 70 }
        }
      });
    }

    // Professional Footer with pagination
    const pageCount = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      addFooter(i, pageCount);
    }

    const fname = filename || `relatorio-veiculo-${vehicle.plate}-${Date.now()}.pdf`;
    doc.save(fname);
  };

  const exportMaintenanceReportPDF = (
    data: any[],
    analytics: MaintenanceReportAnalytics,
    filters?: any,
    filename?: string
  ) => {
    const doc = new jsPDF();
    let yPos = 20;

    // Helper: Professional Footer
    const addFooter = (pageNum: number, totalPages: number) => {
      const pageHeight = doc.internal.pageSize.height;
      doc.setDrawColor(59, 130, 246);
      doc.setLineWidth(0.5);
      doc.line(15, pageHeight - 15, 195, pageHeight - 15);
      
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.setFont('helvetica', 'normal');
      doc.text('FleetONE - Sistema de Gestão de Frotas', 15, pageHeight - 10);
      doc.text(`Página ${pageNum} de ${totalPages}`, 195, pageHeight - 10, { align: 'right' });
      doc.text(`Gerado em: ${new Date().toLocaleDateString('pt-BR')}`, 105, pageHeight - 10, { align: 'center' });
    };

    // Helper: Section Header
    const addSectionHeader = (title: string, currentY: number, fontSize: number = 12): number => {
      doc.setDrawColor(59, 130, 246);
      doc.setFillColor(59, 130, 246);
      doc.rect(15, currentY, 3, 8, 'F');
      
      doc.setFillColor(245, 245, 245);
      doc.rect(18, currentY, 177, 8, 'F');
      
      doc.setFontSize(fontSize);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(40, 40, 40);
      doc.text(title, 22, currentY + 6);
      
      return currentY + 12;
    };

    // Helper: KPI Card
    const addKPICard = (x: number, y: number, label: string, value: string, color: [number, number, number]) => {
      const cardWidth = 42;
      const cardHeight = 18;
      
      // Card shadow
      doc.setFillColor(200, 200, 200);
      doc.rect(x + 0.5, y + 0.5, cardWidth, cardHeight, 'F');
      
      // Card background
      doc.setFillColor(255, 255, 255);
      doc.rect(x, y, cardWidth, cardHeight, 'F');
      
      // Card border
      doc.setDrawColor(color[0], color[1], color[2]);
      doc.setLineWidth(0.5);
      doc.rect(x, y, cardWidth, cardHeight);
      
      // Top accent bar
      doc.setFillColor(color[0], color[1], color[2]);
      doc.rect(x, y, cardWidth, 2.5, 'F');
      
      // Label
      doc.setFontSize(7);
      doc.setTextColor(100, 100, 100);
      doc.setFont('helvetica', 'normal');
      doc.text(label, x + cardWidth / 2, y + 8, { align: 'center' });
      
      // Value
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(40, 40, 40);
      doc.text(value, x + cardWidth / 2, y + 14, { align: 'center' });
    };

    // Table Config
    const tableConfig = {
      styles: {
        fontSize: 8,
        cellPadding: 2.5,
        lineColor: [220, 220, 220] as [number, number, number],
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [59, 130, 246] as [number, number, number],
        textColor: 255,
        fontSize: 9,
        fontStyle: 'bold' as const,
        halign: 'left' as const,
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252] as [number, number, number],
      },
    };

    // PAGE 1: Executive Overview
    doc.setFillColor(59, 130, 246);
    doc.rect(0, 0, 210, 35, 'F');
    
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('FleetONE', 15, 15);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Relatorio de Manutencao', 15, 22);
    
    doc.setFontSize(8);
    doc.text(`Periodo: ${filters?.startDate || 'Inicio'} até ${filters?.endDate || 'Hoje'}`, 195, 20, { align: 'right' });
    doc.text(`Total de registros: ${data.length}`, 195, 26, { align: 'right' });
    
    doc.setDrawColor(255, 200, 0);
    doc.setLineWidth(2);
    doc.line(15, 33, 195, 33);
    
    yPos = 45;
    doc.setTextColor(40, 40, 40);

    // Executive KPI Cards
    const totalCost = data.reduce((sum, item) => sum + (item.cost || 0), 0);
    const completed = data.filter(item => item.status === 'closed').length;
    const open = data.filter(item => item.status !== 'closed').length;
    const pending = analytics.recommendations.filter(r => r.priority === 'high').length;

    const cardStartX = 15;
    const cardSpacing = 4;
    const cardWidth = 42;

    addKPICard(cardStartX, yPos, 'Total de OS', data.length.toString(), [59, 130, 246]);
    addKPICard(cardStartX + (cardWidth + cardSpacing), yPos, 'OS Concluidas', completed.toString(), [16, 185, 129]);
    addKPICard(cardStartX + 2 * (cardWidth + cardSpacing), yPos, 'OS Abertas', open.toString(), [245, 158, 11]);
    addKPICard(cardStartX + 3 * (cardWidth + cardSpacing), yPos, 'Custo Total', formatCurrency(totalCost), [239, 68, 68]);

    yPos += 24;

    // Monthly Trend Table (Top 6)
    if (analytics.monthlyTrend.length > 0) {
      yPos = addSectionHeader('Evolucao Mensal (Ultimos 6 Meses)', yPos);

      const monthlyData = analytics.monthlyTrend.slice(-6).map(m => [
        m.month,
        m.totalOrders.toString(),
        formatCurrency(m.totalCost),
        formatCurrency(m.avgCost)
      ]);

      autoTable(doc, {
        head: [['Mes', 'Total OS', 'Custo Total', 'Custo Medio']],
        body: monthlyData,
        startY: yPos,
        ...tableConfig,
      });

      yPos = (doc as any).lastAutoTable.finalY + 8;
    }

    // Cost by Type
    if (analytics.costByType) {
      yPos = addSectionHeader('Distribuicao por Tipo de Manutencao', yPos);

      const typeData = [
        [
          'Preventiva',
          analytics.costByType.preventive.count.toString(),
          formatCurrency(analytics.costByType.preventive.cost),
          `${analytics.costByType.preventive.percentage.toFixed(1)}%`
        ],
        [
          'Corretiva',
          analytics.costByType.corrective.count.toString(),
          formatCurrency(analytics.costByType.corrective.cost),
          `${analytics.costByType.corrective.percentage.toFixed(1)}%`
        ]
      ];

      autoTable(doc, {
        head: [['Tipo', 'Quantidade', 'Custo Total', 'Percentual']],
        body: typeData,
        startY: yPos,
        ...tableConfig,
      });

      yPos = (doc as any).lastAutoTable.finalY + 8;
    }

    // PAGE 2: Realized Maintenance Overview
    doc.addPage();
    yPos = 20;

    yPos = addSectionHeader('Manutencoes Realizadas - Visao Geral', yPos);

    // Detailed KPIs
    const avgRepairTime = data.filter(d => d.closed_at && d.opened_at).reduce((sum, d) => {
      const days = Math.ceil((new Date(d.closed_at).getTime() - new Date(d.opened_at).getTime()) / (1000 * 60 * 60 * 24));
      return sum + days;
    }, 0) / Math.max(completed, 1);

    const completionRate = (completed / Math.max(data.length, 1)) * 100;
    const avgCost = totalCost / Math.max(data.length, 1);
    const activeProviders = new Set(data.map(d => d.provider_id).filter(Boolean)).size;

    addKPICard(cardStartX, yPos, 'Tempo Medio', `${avgRepairTime.toFixed(1)}d`, [59, 130, 246]);
    addKPICard(cardStartX + (cardWidth + cardSpacing), yPos, 'Taxa Conclusao', `${completionRate.toFixed(0)}%`, [16, 185, 129]);
    addKPICard(cardStartX + 2 * (cardWidth + cardSpacing), yPos, 'Custo Medio/OS', formatCurrency(avgCost), [245, 158, 11]);
    addKPICard(cardStartX + 3 * (cardWidth + cardSpacing), yPos, 'Fornecedores', activeProviders.toString(), [139, 92, 246]);

    yPos += 24;

    // Maintenance Orders Table (Top 15)
    yPos = addSectionHeader('Detalhamento de Ordens de Servico (Top 15)', yPos);

    const woData = data.slice(0, 15).map(wo => [
      wo.order_number ? formatWorkOrderNumber(wo.order_number) : wo.id?.slice(0, 8) || 'N/A',
      wo.vehicle_plate || 'N/A',
      wo.provider_name || 'Sem fornecedor',
      wo.maintenance_type || 'N/A',
      formatCurrency(wo.cost || 0),
      formatDate(wo.opened_at),
      translateStatus(wo.status)
    ]);

    autoTable(doc, {
      head: [['N° OS', 'Veiculo', 'Fornecedor', 'Tipo', 'Custo', 'Abertura', 'Status']],
      body: woData,
      startY: yPos,
      ...tableConfig,
      columnStyles: {
        0: { cellWidth: 18 },
        1: { cellWidth: 18 },
        2: { cellWidth: 35 },
        3: { cellWidth: 48, overflow: 'linebreak' as const },
        4: { cellWidth: 22, halign: 'right' as const },
        5: { cellWidth: 18 },
        6: { cellWidth: 21, overflow: 'linebreak' as const }
      }
    });

    // PAGE 3: Pending Maintenance Analysis
    if (analytics.pendingMaintenance && analytics.pendingMaintenance.totalPending > 0) {
      doc.addPage();
      yPos = 20;

      yPos = addSectionHeader('Manutencoes Pendentes - Analise', yPos);

      // Pending KPIs
      addKPICard(cardStartX, yPos, 'Total Pendentes', analytics.pendingMaintenance.totalPending.toString(), [245, 158, 11]);
      addKPICard(cardStartX + (cardWidth + cardSpacing), yPos, 'Vencidas', analytics.pendingMaintenance.overdue.toString(), [239, 68, 68]);
      addKPICard(cardStartX + 2 * (cardWidth + cardSpacing), yPos, 'Criticas', analytics.pendingMaintenance.critical.toString(), [251, 146, 60]);
      addKPICard(cardStartX + 3 * (cardWidth + cardSpacing), yPos, 'Custo Estimado', formatCurrency(analytics.pendingMaintenance.estimatedCost), [59, 130, 246]);

      yPos += 24;

      // Pending Plans Table
      yPos = addSectionHeader('Planos de Manutencao Pendentes (Top 20)', yPos);

      const pendingData = analytics.pendingMaintenance.plans.slice(0, 20).map(p => {
        let statusText = '[OK] Programada';
        if (p.isOverdue) statusText = '[!] VENCIDA';
        else if (p.isCritical) statusText = '[!] CRITICA';

        return [
          p.vehiclePlate,
          p.planName.substring(0, 30),
          formatDate(p.nextExecutionDate),
          p.daysUntilMaintenance !== null ? `${p.daysUntilMaintenance}d` : '-',
          p.kmUntilMaintenance !== null ? `${p.kmUntilMaintenance.toLocaleString('pt-BR')} km` : '-',
          statusText
        ];
      });

      autoTable(doc, {
        head: [['Veiculo', 'Plano', 'Proxima Data', 'Dias', 'KM Restante', 'Status']],
        body: pendingData,
        startY: yPos,
        ...tableConfig,
        columnStyles: {
          0: { cellWidth: 20 },
          1: { cellWidth: 55, overflow: 'linebreak' as const },
          2: { cellWidth: 25 },
          3: { cellWidth: 18 },
          4: { cellWidth: 28 },
          5: { cellWidth: 34, overflow: 'linebreak' as const }
        }
      });

      yPos = (doc as any).lastAutoTable.finalY + 12;

      // Distribution by urgency
      const overdueCount = analytics.pendingMaintenance.overdue;
      const criticalCount = analytics.pendingMaintenance.critical;
      const scheduledCount = analytics.pendingMaintenance.totalPending - overdueCount - criticalCount;

      yPos = addSectionHeader('Distribuicao por Urgencia', yPos);

      const urgencyData = [
        ['[!] Vencidas', overdueCount.toString(), `${((overdueCount / Math.max(analytics.pendingMaintenance.totalPending, 1)) * 100).toFixed(1)}%`],
        ['[!] Criticas (<=7 dias ou <=500 km)', criticalCount.toString(), `${((criticalCount / Math.max(analytics.pendingMaintenance.totalPending, 1)) * 100).toFixed(1)}%`],
        ['[OK] Programadas', scheduledCount.toString(), `${((scheduledCount / Math.max(analytics.pendingMaintenance.totalPending, 1)) * 100).toFixed(1)}%`]
      ];

      autoTable(doc, {
        head: [['Status', 'Quantidade', 'Percentual']],
        body: urgencyData,
        startY: yPos,
        ...tableConfig,
      });
    }

    // PAGE 4: Provider Performance
    doc.addPage();
    yPos = 20;

    yPos = addSectionHeader('Analise de Performance de Fornecedores', yPos);

    if (analytics.providerPerformance.length > 0) {
      const providerData = analytics.providerPerformance.map(p => [
        p.providerName,
        p.totalOrders.toString(),
        formatCurrency(p.totalCost),
        formatCurrency(p.avgCost),
        `${p.completionRate.toFixed(0)}%`,
        `${p.avgRepairDays.toFixed(1)}d`
      ]);

      autoTable(doc, {
        head: [['Fornecedor', 'Total OS', 'Custo Total', 'Custo Medio', 'Taxa Concl.', 'Tempo Medio']],
        body: providerData,
        startY: yPos,
        ...tableConfig,
      });

      yPos = (doc as any).lastAutoTable.finalY + 12;
    }

    // Critical Vehicles
    if (analytics.criticalVehicles.length > 0) {
      yPos = addSectionHeader('Top 10 Veiculos por Custo de Manutencao', yPos);

      const criticalData = analytics.criticalVehicles.slice(0, 10).map(v => [
        v.vehiclePlate,
        v.maintenanceCount.toString(),
        formatCurrency(v.totalCost),
        formatCurrency(v.totalCost / Math.max(v.maintenanceCount, 1)),
        formatDate(v.lastMaintenance)
      ]);

      autoTable(doc, {
        head: [['Placa', 'Total OS', 'Custo Total', 'Custo Medio', 'Ultima Manutencao']],
        body: criticalData,
        startY: yPos,
        ...tableConfig,
      });
    }

    // PAGE 5: Timeline of Events
    doc.addPage();
    yPos = 20;

    yPos = addSectionHeader('Timeline de Manutencoes (Ultimos 30 Eventos)', yPos);

    if (analytics.timelineEvents.length > 0) {
      const typeIconMap: Record<string, string> = {
        maintenance: '[M]',
        alert: '[!]',
        inspection: '[I]'
      };

      const timelineData = analytics.timelineEvents.slice(0, 30).map(evt => [
        formatDate(evt.date),
        evt.orderNumber ? formatWorkOrderNumber(evt.orderNumber) : '-',
        evt.vehiclePlate,
        `${typeIconMap[evt.type] || ''} ${evt.title.substring(0, 25)}`,
        evt.provider || '-',
        translateStatus(evt.status),
        evt.cost ? formatCurrency(evt.cost) : '-'
      ]);

      autoTable(doc, {
        head: [['Data', 'N° OS', 'Veiculo', 'Tipo', 'Fornecedor', 'Status', 'Custo']],
        body: timelineData,
        startY: yPos,
        ...tableConfig,
        columnStyles: {
          3: { cellWidth: 40 }
        }
      });
    }

    // PAGE 6: Recommendations
    doc.addPage();
    yPos = 20;

    yPos = addSectionHeader('Predicoes e Recomendacoes Estrategicas', yPos);

    if (analytics.recommendations.length > 0) {
      const priorityIcon: Record<string, string> = {
        high: '[H]',
        medium: '[M]',
        low: '[L]'
      };

      const priorityText: Record<string, string> = {
        high: 'ALTA',
        medium: 'MEDIA',
        low: 'BAIXA'
      };

      const categoryMap: Record<string, string> = {
        cost: 'Custo',
        efficiency: 'Eficiencia',
        safety: 'Seguranca',
        provider: 'Fornecedor'
      };

      const recData = analytics.recommendations.slice(0, 15).map(r => [
        `${priorityIcon[r.priority]} ${priorityText[r.priority]}`,
        categoryMap[r.category] || r.category,
        r.title.substring(0, 30),
        r.description.substring(0, 60) + (r.description.length > 60 ? '...' : ''),
        r.potentialSavings ? formatCurrency(r.potentialSavings) : '-',
        r.affectedVehicles?.toString() || '-'
      ]);

      autoTable(doc, {
        head: [['Prioridade', 'Categoria', 'Acao', 'Descricao', 'Economia', 'Veiculos']],
        body: recData,
        startY: yPos,
        ...tableConfig,
        columnStyles: {
          0: { cellWidth: 20 },
          1: { cellWidth: 22 },
          2: { cellWidth: 35, overflow: 'linebreak' as const },
          3: { cellWidth: 70, overflow: 'linebreak' as const },
          4: { cellWidth: 22 },
          5: { cellWidth: 15 }
        }
      });
    }

    // PAGE 7: Alerts and Immediate Actions
    if (analytics.pendingMaintenance && (analytics.pendingMaintenance.overdue > 0 || analytics.pendingMaintenance.critical > 0)) {
      doc.addPage();
      yPos = 20;

      yPos = addSectionHeader('Alertas e Acoes Imediatas', yPos);

      // Overdue Maintenance
      if (analytics.pendingMaintenance.overdue > 0) {
        yPos = addSectionHeader('[!] Manutencoes Vencidas (Requer Acao Imediata)', yPos, 12);

        const overdueData = analytics.pendingMaintenance.plans
          .filter(p => p.isOverdue)
          .slice(0, 10)
          .map(p => [
            p.vehiclePlate,
            p.planName.substring(0, 35),
            formatDate(p.nextExecutionDate),
            p.daysUntilMaintenance !== null ? `${Math.abs(p.daysUntilMaintenance)}d atrasado` : '-',
            p.kmUntilMaintenance !== null ? `${Math.abs(p.kmUntilMaintenance).toLocaleString('pt-BR')} km` : '-'
          ]);

        autoTable(doc, {
          head: [['Veiculo', 'Plano', 'Data Prevista', 'Atraso', 'KM Excedido']],
          body: overdueData,
          startY: yPos,
          ...tableConfig,
          headStyles: { fillColor: [239, 68, 68] }
        });

        yPos = (doc as any).lastAutoTable.finalY + 10;
      }

      // Critical Maintenance
      if (analytics.pendingMaintenance.critical > 0) {
        yPos = addSectionHeader('[!] Manutencoes Criticas (Proximos 7 dias)', yPos, 12);

        const criticalData = analytics.pendingMaintenance.plans
          .filter(p => p.isCritical && !p.isOverdue)
          .slice(0, 10)
          .map(p => [
            p.vehiclePlate,
            p.planName.substring(0, 35),
            formatDate(p.nextExecutionDate),
            p.daysUntilMaintenance !== null ? `${p.daysUntilMaintenance}d` : '-',
            p.kmUntilMaintenance !== null ? `${p.kmUntilMaintenance.toLocaleString('pt-BR')} km` : '-'
          ]);

        autoTable(doc, {
          head: [['Veiculo', 'Plano', 'Data Prevista', 'Dias Restantes', 'KM Restante']],
          body: criticalData,
          startY: yPos,
          ...tableConfig,
          headStyles: { fillColor: [251, 146, 60] }
        });
      }
    }

    // PAGE 8: Summary and Next Steps
    doc.addPage();
    yPos = 20;

    yPos = addSectionHeader('Resumo e Proximos Passos', yPos);

    // Consolidated KPIs
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('KPIs Consolidados:', 20, yPos);
    yPos += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`• Total de Ordens de Servico: ${data.length}`, 25, yPos);
    yPos += 6;
    doc.text(`• Ordens Concluidas: ${completed} (${completionRate.toFixed(1)}%)`, 25, yPos);
    yPos += 6;
    doc.text(`• Custo Total: ${formatCurrency(totalCost)}`, 25, yPos);
    yPos += 6;
    doc.text(`• Manutencoes Pendentes: ${analytics.pendingMaintenance?.totalPending || 0}`, 25, yPos);
    yPos += 6;
    doc.text(`• Pendencias Criticas: ${(analytics.pendingMaintenance?.overdue || 0) + (analytics.pendingMaintenance?.critical || 0)}`, 25, yPos);
    yPos += 10;

    // Top 3 Insights
    yPos = addSectionHeader('Top 3 Acoes Recomendadas', yPos);

    const topRecommendations = analytics.recommendations
      .filter(r => r.priority === 'high')
      .slice(0, 3);

    if (topRecommendations.length > 0) {
      topRecommendations.forEach((rec, idx) => {
        doc.setFont('helvetica', 'bold');
        doc.text(`${idx + 1}. ${rec.title}`, 25, yPos);
        yPos += 6;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        const descLines = doc.splitTextToSize(rec.description, 160);
        doc.text(descLines, 30, yPos);
        yPos += descLines.length * 5 + 4;
        doc.setFontSize(10);
      });
    }

    yPos += 6;

    // Future Projections
    yPos = addSectionHeader('Projecoes - Proximos 30 Dias', yPos);

    const next30DaysPending = analytics.pendingMaintenance?.plans.filter(p => {
      return p.daysUntilMaintenance !== null && p.daysUntilMaintenance >= 0 && p.daysUntilMaintenance <= 30;
    }).length || 0;

    doc.text(`• Manutencoes Programadas: ${next30DaysPending}`, 25, yPos);
    yPos += 6;
    doc.text(`• Custo Estimado: ${formatCurrency(analytics.pendingMaintenance?.estimatedCost || 0)}`, 25, yPos);
    yPos += 6;
    doc.text(`• Veiculos Criticos: ${analytics.criticalVehicles.length}`, 25, yPos);

    // Professional Footer with pagination
    const pageCount = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      addFooter(i, pageCount);
    }

    const fname = filename || `relatorio-manutencao-${Date.now()}.pdf`;
    doc.save(fname);
  };

  const exportReport = async (config: ExportConfig) => {
    setIsExporting(true);
    
    try {
      // Check if vehicle-detail report with dedicated data
      const isVehicleDetail = config.type === 'vehicle-detail' && !!config.vehicleDetailData;
      
      // Validate data before export (skip for vehicle-detail with vehicleDetailData)
      if ((!config.data || config.data.length === 0) && !isVehicleDetail) {
        toast({
          title: "Nenhum dado para exportar",
          description: "Não há dados disponíveis para gerar o relatório. Ajuste os filtros e tente novamente.",
          variant: "destructive",
        });
        return;
      }

      // Validate report type
      const reportConfig = getReportConfig(config.type);
      if (!reportConfig && config.type !== 'vehicle-detail') {
        throw new Error(`Tipo de relatório '${config.type}' não suportado`);
      }

      switch (config.format) {
        case 'excel':
          await exportToExcel(config);
          break;
        case 'csv':
          await exportToCSV(config);
          break;
        case 'pdf':
          await exportToPDF(config);
          break;
        default:
          throw new Error('Formato não suportado');
      }
      
      toast({
        title: "Exportação concluída",
        description: `Relatório exportado com sucesso em formato ${config.format.toUpperCase()}`,
      });
    } catch (error) {
      console.error('Erro na exportação:', error);
      toast({
        title: "Erro na exportação",
        description: error instanceof Error ? error.message : "Não foi possível exportar o relatório.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  // =============================
  // Relatório Consolidado (FleetONE)
  // LAYOUT PROFISSIONAL - Matching HTML Preview
  // =============================
  const generateConsolidatedPDF = async (doc: jsPDF, config: ExportConfig) => {
    const { filters, company, fleetReportData } = config;
    const primaryColor = hexToRgb(company?.branding?.primary_color || '#2563eb');
    const pageHeight = doc.internal.pageSize.height;
    const pageWidth = doc.internal.pageSize.width;

    // Import helpers
    const {
      addModernCoverPage,
      addKpiCardsWithBadges,
      addModernSectionHeader,
      addInsightsList,
      addAlertSummaryBadges,
      addRankingTable,
      addConclusionBox,
      addModernFooter,
      addPageHeader,
      BADGE_COLORS,
    } = await import('./report-pdf-helpers');

    // Helper: Gerar insights no frontend se RPC não enviar
    const generateFrontendInsights = (data: any): string[] => {
      const insights: string[] = [];
      const kpis = data?.summaryKpis || {};
      const vehicles = data?.byVehicle?.rows || [];

      if (vehicles.length >= 3) {
        const top3Cost = vehicles.slice(0, 3).reduce((sum: number, v: any) => sum + (v.kpis?.totalCost || 0), 0);
        const totalCost = kpis.totalCost || 0;
        if (totalCost > 0) {
          const pct = Math.round((top3Cost / totalCost) * 100);
          insights.push(`Top 3 veiculos concentram ${pct}% do custo total da frota.`);
        }
      }

      const withCostKm = vehicles.filter((v: any) => (v.kpis?.costPerKm || 0) > 0);
      if (withCostKm.length > 0) {
        const worst = withCostKm.sort((a: any, b: any) => (b.kpis?.costPerKm || 0) - (a.kpis?.costPerKm || 0))[0];
        const plate = worst.vehicle_label?.split(' - ')[0] || 'N/A';
        insights.push(`Maior custo/km: ${plate} - R$ ${(worst.kpis?.costPerKm || 0).toFixed(2)}/km.`);
      }

      if (kpis.fuelPercent) {
        insights.push(`Combustivel representa ${kpis.fuelPercent}% do custo total.`);
      }

      if (kpis.criticalAlerts && kpis.criticalAlerts > 0) {
        insights.push(`${kpis.criticalAlerts} alertas criticos ativos: risco operacional imediato.`);
      }

      if (kpis.avgCostPerVehicle) {
        insights.push(`Custo medio por veiculo: R$ ${Number(kpis.avgCostPerVehicle).toLocaleString('pt-BR')} no periodo.`);
      }

      if (kpis.expiredDocuments && kpis.expiredDocuments > 0) {
        insights.push(`${kpis.expiredDocuments} documentos vencidos: regularizar para evitar multas.`);
      }

      return insights.slice(0, 7);
    };

    // Helper: Gerar conclusão automática
    const generateAutoConclusion = (data: any) => {
      const kpis = data?.summaryKpis || {};
      const vehicles = data?.byVehicle?.rows || [];
      const recommendations: string[] = [];

      if (vehicles.length >= 3) {
        const top3Labels = vehicles.slice(0, 3).map((v: any) => (v.vehicle_label?.split(' - ')[0] || 'N/A'));
        recommendations.push(`Revisar operacao dos veiculos ${top3Labels.join(', ')} (maiores custos).`);
      }

      if ((kpis.fuelPercent || 0) > 60) {
        recommendations.push('Avaliar eficiencia de combustivel e rotas da frota.');
      }

      if ((kpis.costPerKm || 0) > 2) {
        recommendations.push('Auditar custos operacionais - custo/km acima do esperado.');
      }

      if ((kpis.criticalAlerts || 0) > 0) {
        recommendations.push('Resolver alertas criticos antes de continuar operacao normal.');
      }

      if ((kpis.expiredDocuments || 0) > 0) {
        recommendations.push('Regularizar documentos vencidos para reduzir risco legal.');
      }

      recommendations.push('Monitorar evolucao do custo/km nos proximos 30 dias.');
      recommendations.push('Agendar revisao de indicadores em 15 dias.');

      const summary = `A frota apresenta ${kpis.activeVehicles || 0} veiculos ativos com custo total de R$ ${Number(kpis.totalCost || 0).toLocaleString('pt-BR')} no periodo analisado. ` +
        `O custo medio por km e de R$ ${Number(kpis.costPerKm || 0).toFixed(2)}, com ${kpis.fuelPercent || 0}% do custo concentrado em combustivel e ${kpis.maintenancePercent || 0}% em manutencao.`;

      return { summary, recommendations };
    };

    // Se não tiver dados estruturados
    if (!fleetReportData) {
      doc.setFontSize(14);
      doc.setTextColor(100, 100, 100);
      doc.text('Dados do relatorio nao disponiveis. Gere o relatorio novamente.', pageWidth / 2, pageHeight / 2, { align: 'center' });
      return;
    }

    const kpis = fleetReportData.summaryKpis || {};
    const byVehicle = fleetReportData.byVehicle || { rows: [], totals: {} };
    const byDriver = fleetReportData.byDriver || { rows: [], totals: {} };
    const alerts = fleetReportData.alerts || [];
    const insights = (fleetReportData.insights && fleetReportData.insights.length > 0) 
      ? fleetReportData.insights 
      : generateFrontendInsights(fleetReportData);

    const period = fleetReportData.metadata?.period;

    // ====== PÁGINA 1: CAPA EXECUTIVA MODERNA ======
    await addModernCoverPage(doc, {
      reportTitle: 'RELATORIO CONSOLIDADO DA FROTA',
      reportSubtitle: 'Visao Gerencial e Operacional',
      company,
      period,
      primaryColor
    });

    // ====== PÁGINA 2: RESUMO EXECUTIVO COM KPI CARDS ======
    doc.addPage();
    let yPos = addPageHeader(doc, 'RESUMO EXECUTIVO', primaryColor);

    // KPI Cards com badges (matching HTML preview)
    const getCostPerKmBadge = () => {
      const v = Number(kpis.costPerKm || 0);
      if (v >= 2.5) return { label: 'Critico', color: BADGE_COLORS.critical };
      if (v >= 1.5) return { label: 'Atencao', color: BADGE_COLORS.warning };
      return undefined;
    };

    const getAlertsBadge = () => {
      const v = Number(kpis.criticalAlerts || 0);
      if (v > 0) return { label: 'Acao imediata', color: BADGE_COLORS.action };
      return { label: 'Estavel', color: BADGE_COLORS.ok };
    };

    const kpiCards = [
      { 
        label: 'Veiculos Ativos', 
        value: String(kpis.activeVehicles || kpis.totalVehicles || 0),
        subtitle: `Total: ${kpis.totalVehicles || 0}`,
        accentColor: [59, 130, 246] as [number, number, number]
      },
      { 
        label: 'Km no Periodo', 
        value: Number(kpis.totalKm || 0).toLocaleString('pt-BR'),
        subtitle: `Viagens: ${Number(kpis.totalTrips || 0).toLocaleString('pt-BR')}`,
        accentColor: [16, 185, 129] as [number, number, number]
      },
      { 
        label: 'Custo Total', 
        value: `R$ ${Number(kpis.totalCost || 0).toLocaleString('pt-BR')}`,
        subtitle: `Media/veiculo: R$ ${Number(kpis.avgCostPerVehicle || 0).toLocaleString('pt-BR')}`,
        accentColor: [245, 158, 11] as [number, number, number]
      },
      { 
        label: 'Custo / Km', 
        value: `R$ ${Number(kpis.costPerKm || 0).toFixed(2)}`,
        subtitle: 'Meta: < R$ 2,50',
        badge: getCostPerKmBadge(),
        accentColor: Number(kpis.costPerKm || 0) >= 2.5 ? BADGE_COLORS.critical : Number(kpis.costPerKm || 0) >= 1.5 ? BADGE_COLORS.warning : [16, 185, 129] as [number, number, number]
      },
      { 
        label: '% Combustivel', 
        value: `${kpis.fuelPercent || 0}%`,
        subtitle: `Manutencao: ${kpis.maintenancePercent || 0}%`,
        accentColor: [139, 92, 246] as [number, number, number]
      },
      { 
        label: 'Riscos', 
        value: `${kpis.criticalAlerts || 0} criticos`,
        subtitle: `${kpis.expiredDocuments || 0} docs vencidos`,
        badge: getAlertsBadge(),
        accentColor: Number(kpis.criticalAlerts || 0) > 0 ? BADGE_COLORS.critical : [16, 185, 129] as [number, number, number]
      }
    ];

    yPos = addKpiCardsWithBadges(doc, kpiCards, yPos, primaryColor);

    // Indicadores complementares
    yPos += 5;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(60, 60, 60);
    doc.text('Indicadores Complementares', 14, yPos);
    yPos += 8;

    autoTable(doc, {
      body: [
        ['Total de Veiculos', String(kpis.totalVehicles || 0)],
        ['Veiculos com Alertas', String(kpis.vehiclesWithAlerts || 0)],
        ['Documentos Vencidos', String(kpis.expiredDocuments || 0)],
        ['Total de Viagens', String(kpis.totalTrips || 0)]
      ],
      startY: yPos,
      theme: 'plain',
      margin: { left: 14 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 80 },
        1: { cellWidth: 60, halign: 'right' }
      },
      styles: { fontSize: 9, cellPadding: 2 }
    });

    // ====== PÁGINA 3: INSIGHTS AUTOMÁTICOS ======
    doc.addPage();
    yPos = addPageHeader(doc, 'PRINCIPAIS INSIGHTS DO PERIODO', primaryColor);

    yPos = addModernSectionHeader(doc, 'Insights Automaticos', 'Textos automaticos para leitura gerencial e acao', yPos, primaryColor);
    yPos = addInsightsList(doc, insights, yPos, primaryColor);

    // Pontos de atenção
    yPos += 10;
    const attentionPoints: string[] = [];
    if ((kpis.criticalAlerts || 0) > 0) attentionPoints.push(`${kpis.criticalAlerts} alertas criticos requerem acao imediata`);
    if ((kpis.expiredDocuments || 0) > 0) attentionPoints.push(`${kpis.expiredDocuments} documentos vencidos na frota`);
    if ((kpis.costPerKm || 0) > 2) attentionPoints.push(`Custo/km elevado (R$ ${Number(kpis.costPerKm).toFixed(2)}) - acima do padrao`);
    if ((kpis.fuelPercent || 0) > 70) attentionPoints.push(`Combustivel representa ${kpis.fuelPercent}% do custo - avaliar eficiencia`);

    if (attentionPoints.length > 0) {
      yPos = addModernSectionHeader(doc, 'Pontos de Atencao', null, yPos, { r: 220, g: 38, b: 38 });
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(80, 80, 80);
      attentionPoints.forEach(point => {
        doc.setFillColor(220, 38, 38);
        doc.circle(18, yPos - 1.5, 1.5, 'F');
        doc.text(point, 24, yPos);
        yPos += 8;
      });
    } else {
      doc.setFillColor(240, 255, 240);
      doc.roundedRect(14, yPos, 180, 20, 2, 2, 'F');
      doc.setFontSize(10);
      doc.setTextColor(16, 185, 129);
      doc.text('Nenhum ponto critico identificado no periodo.', 20, yPos + 12);
    }

    // ====== PÁGINA 4: ANÁLISE POR VEÍCULO (RANKING) ======
    doc.addPage();
    yPos = addPageHeader(doc, 'ANALISE POR VEICULO', primaryColor);

    yPos = addModernSectionHeader(doc, 'Ranking por Custo Total', `Top ${Math.min(byVehicle.rows.length, 20)} veiculos`, yPos, primaryColor);

    if (byVehicle.rows.length > 0) {
      const vehicleHeaders = ['#', 'Veiculo', 'Status', 'Km', 'Custo Total', 'R$/Km', '% Frota', 'Alertas'];
      const vehicleData = byVehicle.rows.slice(0, 20).map((row: any, idx: number) => [
        String(idx + 1),
        (row.vehicle_label || 'N/A').substring(0, 22),
        row.kpis?.status === 'active' ? 'Ativo' : 'Inativo',
        Number(row.kpis?.totalKm || 0).toLocaleString('pt-BR'),
        `R$ ${Number(row.kpis?.totalCost || 0).toLocaleString('pt-BR')}`,
        `R$ ${Number(row.kpis?.costPerKm || 0).toFixed(2)}`,
        `${Number(row.kpis?.fleetPercent || 0).toFixed(1)}%`,
        Number(row.kpis?.alertCount || 0) > 0 ? `! ${row.kpis?.alertCount}` : 'OK'
      ]);

      yPos = addRankingTable(doc, vehicleHeaders, vehicleData, yPos, primaryColor, {
        highlightTop3: true,
        columnStyles: {
          0: { cellWidth: 10, halign: 'center' },
          1: { cellWidth: 40 },
          2: { cellWidth: 18, halign: 'center' },
          3: { cellWidth: 22, halign: 'right' },
          4: { cellWidth: 28, halign: 'right' },
          5: { cellWidth: 20, halign: 'right' },
          6: { cellWidth: 18, halign: 'right' },
          7: { cellWidth: 18, halign: 'center' }
        }
      });

      // Totalizador
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(60, 60, 60);
      doc.text(`TOTAL: ${byVehicle.rows.length} veiculos | R$ ${Number(byVehicle.totals.totalCost || 0).toLocaleString('pt-BR')} | ${Number(byVehicle.totals.totalKm || 0).toLocaleString('pt-BR')} km`, 14, yPos);
    } else {
      doc.setFontSize(11);
      doc.setTextColor(150, 150, 150);
      doc.text('Nenhum veiculo encontrado no periodo.', 14, yPos);
    }

    // ====== PÁGINA 5: ANÁLISE POR MOTORISTA ======
    doc.addPage();
    yPos = addPageHeader(doc, 'ANALISE POR MOTORISTA', primaryColor);

    yPos = addModernSectionHeader(doc, 'Performance Operacional', 'Viagens, Km, Custo e Ocorrencias', yPos, primaryColor);

    const hasDriverData = byDriver.rows.some((d: any) => (d.kpis?.tripCount || 0) > 0 || (d.kpis?.totalKm || 0) > 0);

    if (!hasDriverData) {
      doc.setFillColor(255, 250, 240);
      doc.roundedRect(14, yPos, 180, 30, 2, 2, 'F');
      doc.setDrawColor(245, 158, 11);
      doc.setLineWidth(0.5);
      doc.roundedRect(14, yPos, 180, 30, 2, 2, 'S');

      doc.setFontSize(10);
      doc.setTextColor(180, 100, 0);
      doc.text('Nao ha dados suficientes de viagens associadas aos motoristas', 20, yPos + 12);
      doc.text('no periodo selecionado. Verifique a integracao de telemetria.', 20, yPos + 22);
    } else if (byDriver.rows.length > 0) {
      const driverHeaders = ['#', 'Motorista', 'Viagens', 'Km', 'Custo', 'R$/Km', 'Ocorrencias'];
      const driverData = byDriver.rows.slice(0, 20).map((row: any, idx: number) => [
        String(idx + 1),
        (row.driver_label || 'N/A').substring(0, 25),
        String(row.kpis?.tripCount || 0),
        Number(row.kpis?.totalKm || 0).toLocaleString('pt-BR'),
        `R$ ${Number(row.kpis?.totalCost || 0).toLocaleString('pt-BR')}`,
        `R$ ${Number(row.kpis?.costPerKm || 0).toFixed(2)}`,
        String(row.kpis?.occurrences || row.kpis?.alertCount || 0)
      ]);

      yPos = addRankingTable(doc, driverHeaders, driverData, yPos, primaryColor, {
        columnStyles: {
          0: { cellWidth: 10, halign: 'center' },
          1: { cellWidth: 50 },
          2: { cellWidth: 20, halign: 'center' },
          3: { cellWidth: 25, halign: 'right' },
          4: { cellWidth: 28, halign: 'right' },
          5: { cellWidth: 20, halign: 'right' },
          6: { cellWidth: 22, halign: 'center' }
        }
      });

      // Totalizador
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(60, 60, 60);
      doc.text(`TOTAL: ${byDriver.rows.length} motoristas | ${Number(byDriver.totals.totalTrips || 0)} viagens | ${Number(byDriver.totals.totalKm || 0).toLocaleString('pt-BR')} km`, 14, yPos);
    }

    // ====== PÁGINA 6: ALERTAS E RISCOS ======
    doc.addPage();
    yPos = addPageHeader(doc, 'ALERTAS E RISCOS OPERACIONAIS', primaryColor);

    // Contagem de alertas
    const criticalAlerts = alerts.filter((a: any) => a.severity === 'critical' || a.priority === 'critical');
    const highAlerts = alerts.filter((a: any) => a.severity === 'high' || a.priority === 'high');
    const mediumAlerts = alerts.filter((a: any) => a.severity === 'warning' || a.severity === 'medium' || a.priority === 'medium');
    const lowAlerts = alerts.filter((a: any) => !['critical', 'high', 'warning', 'medium'].includes(a.severity || a.priority));

    yPos = addModernSectionHeader(doc, 'Classificacao por Severidade', null, yPos, primaryColor);

    // Badges de resumo
    yPos = addAlertSummaryBadges(doc, {
      critical: criticalAlerts.length,
      high: highAlerts.length,
      medium: mediumAlerts.length,
      low: lowAlerts.length
    }, yPos);

    yPos += 5;

    const renderAlertSection = (title: string, alertList: any[], color: [number, number, number]) => {
      if (alertList.length === 0) return;

      doc.setFillColor(color[0], color[1], color[2]);
      doc.rect(14, yPos - 4, 4, 7, 'F');
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(color[0], color[1], color[2]);
      doc.text(`${title} (${alertList.length})`, 20, yPos);
      yPos += 7;

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(60, 60, 60);
      alertList.slice(0, 5).forEach((alert: any) => {
        const text = (alert.title || alert.message || 'Alerta').substring(0, 80);
        doc.text(`- ${text}`, 22, yPos);
        yPos += 6;
      });

      yPos += 6;
    };

    if (alerts.length === 0) {
      doc.setFillColor(240, 255, 240);
      doc.roundedRect(14, yPos, 180, 25, 2, 2, 'F');
      doc.setFontSize(11);
      doc.setTextColor(16, 185, 129);
      doc.text('Nenhum alerta ativo no periodo selecionado.', 24, yPos + 15);
    } else {
      renderAlertSection('CRITICOS', criticalAlerts, [220, 38, 38]);
      renderAlertSection('ALTA PRIORIDADE', highAlerts, [234, 88, 12]);
      renderAlertSection('ATENCAO', mediumAlerts, [245, 158, 11]);
      renderAlertSection('INFORMATIVOS', lowAlerts, [100, 116, 139]);
    }

    // ====== PÁGINA 7: CONCLUSÃO GERENCIAL ======
    doc.addPage();
    yPos = addPageHeader(doc, 'CONCLUSAO EXECUTIVA', primaryColor);

    const conclusion = generateAutoConclusion(fleetReportData);
    yPos = addConclusionBox(doc, conclusion, yPos, primaryColor);

    // Próximos passos
    yPos += 10;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b);
    doc.text('Proximos Passos', 14, yPos);
    yPos += 10;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    const nextSteps = [
      '1. Revisar veiculos com maior custo operacional (top 3)',
      '2. Resolver alertas criticos pendentes antes de novas operacoes',
      '3. Agendar reuniao de acompanhamento em 30 dias',
      '4. Monitorar evolucao dos KPIs principais semanalmente'
    ];
    nextSteps.forEach(step => {
      doc.text(step, 18, yPos);
      yPos += 8;
    });

    // ====== ADICIONAR RODAPÉS EM TODAS AS PÁGINAS ======
    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      addModernFooter(doc, i, totalPages, 'Relatorio Consolidado', company, primaryColor);
    }
  };

  // =============================
  // Relatório de Renovação de Frota (FleetONE)
  // =============================
  const generateFleetRenewalPDF = async (doc: jsPDF, config: ExportConfig) => {
    const { filters, company, fleetReportData } = config;

    let yPos = await addProfessionalHeader(doc, {
      reportType: 'fleet-renewal',
      reportTitle: 'Análise de Renovação de Frota',
      company,
      filters
    });

    const primaryColor = hexToRgb(company?.branding?.primary_color || '#2563eb');

    if (!fleetReportData) {
      doc.setFontSize(11);
      doc.setTextColor(100, 100, 100);
      doc.text('Dados do relatório não disponíveis.', 14, yPos);
      return;
    }

    // KPIs
    const kpis = fleetReportData.summaryKpis || {};
    if (Object.keys(kpis).length > 0) {
      doc.setFillColor(primaryColor.r, primaryColor.g, primaryColor.b);
      doc.rect(14, yPos - 5, 4, 8, 'F');
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b);
      doc.text('INDICADORES DE RENOVACAO', 20, yPos);
      yPos += 10;

      const kpiRows = Object.entries(kpis).map(([key, value]) => [
        key.replace(/_/g, ' ').toUpperCase(),
        typeof value === 'number' ? value.toLocaleString('pt-BR') : String(value)
      ]);

      autoTable(doc, {
        body: kpiRows.slice(0, 6),
        startY: yPos,
        theme: 'plain',
        margin: { left: 20 },
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 90 },
          1: { cellWidth: 70, halign: 'right' }
        },
        styles: { fontSize: 10, cellPadding: 3 }
      });

      yPos = (doc as any).lastAutoTable.finalY + 15;
    }

    // Por Veículo (OBRIGATÓRIO) - Análise de Renovação
    const byVehicle = fleetReportData.byVehicle;
    if (byVehicle?.rows?.length > 0) {
      if (yPos > 200) { doc.addPage(); yPos = 20; }

      doc.setFillColor(primaryColor.r, primaryColor.g, primaryColor.b);
      doc.rect(14, yPos - 5, 4, 8, 'F');
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b);
      doc.text('ANALISE POR VEICULO', 20, yPos);
      yPos += 10;

      const headers = ['Veículo', 'Ano', 'Km', 'Custo Manut.', 'Status'];
      const data = byVehicle.rows.slice(0, 25).map((row: any) => [
        row.vehicle_label || 'N/A',
        row.kpis?.year?.toString() || '-',
        (row.kpis?.distance || 0).toLocaleString('pt-BR'),
        `R$ ${(row.kpis?.maintenanceCost || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0 })}`,
        row.kpis?.status || 'N/A'
      ]);

      autoTable(doc, {
        head: [headers],
        body: data,
        startY: yPos,
        ...getTableStyles(company),
        columnStyles: {
          0: { cellWidth: 45 },
          1: { cellWidth: 25, halign: 'center' },
          2: { cellWidth: 35, halign: 'right' },
          3: { cellWidth: 40, halign: 'right' },
          4: { cellWidth: 35, halign: 'center' }
        }
      });

      yPos = (doc as any).lastAutoTable.finalY + 15;
    }

    // Por Motorista (OBRIGATÓRIO)
    const byDriver = fleetReportData.byDriver;
    if (byDriver?.rows?.length > 0) {
      if (yPos > 200) { doc.addPage(); yPos = 20; }

      doc.setFillColor(primaryColor.r, primaryColor.g, primaryColor.b);
      doc.rect(14, yPos - 5, 4, 8, 'F');
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b);
      doc.text('IMPACTO POR MOTORISTA', 20, yPos);
      yPos += 10;

      const driverHeaders = ['Motorista', 'Veículos', 'Custo Médio'];
      const driverData = byDriver.rows.slice(0, 15).map((row: any) => [
        row.driver_label || 'N/A',
        (row.kpis?.vehicleCount || 1).toString(),
        `R$ ${(row.kpis?.avgCost || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0 })}`
      ]);

      autoTable(doc, {
        head: [driverHeaders],
        body: driverData,
        startY: yPos,
        ...getTableStyles(company),
        columnStyles: {
          0: { cellWidth: 70 },
          1: { cellWidth: 40, halign: 'center' },
          2: { cellWidth: 50, halign: 'right' }
        }
      });

      yPos = (doc as any).lastAutoTable.finalY + 15;
    }

    // Items detalhados (documentos, etc)
    const items = fleetReportData.items;
    if (items?.length > 0) {
      if (yPos > 200) { doc.addPage(); yPos = 20; }

      doc.setFillColor(245, 158, 11);
      doc.rect(14, yPos - 5, 4, 8, 'F');
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(245, 158, 11);
      doc.text('ITENS PARA ATENCAO', 20, yPos);
      yPos += 10;

      const itemData = items.slice(0, 15).map((item: any) => [
        item.type || item.category || 'Item',
        item.description || item.name || '-',
        item.expiry_date || item.due_date || '-'
      ]);

      autoTable(doc, {
        head: [['Tipo', 'Descrição', 'Vencimento']],
        body: itemData,
        startY: yPos,
        ...getTableStyles(company),
        headStyles: {
          fillColor: [245, 158, 11] as [number, number, number],
          textColor: [255, 255, 255] as [number, number, number],
          fontStyle: 'bold' as const,
          fontSize: 10
        }
      });
    }
  };

  return {
    exportReport,
    generatePdfBlob,
    isExporting,
  };
};